import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import Header from "./components/Header";
import AppLoader from "./components/AppLoader";
import EtlPipelinePanel from "./components/EtlPipelinePanel";
import KpiGrid from "./components/KpiGrid";
import CustomerTable from "./components/CustomerTable";
import ChartsSection from "./components/ChartsSection";
import Toast from "./components/Toast";
import SegmentActionModal from "./components/SegmentActionModal";
import DeveloperDashboard from "./components/DeveloperDashboardPremium";
import ClientTeamDashboard from "./components/ClientTeamDashboard";
import PhoneCaptureModal from "./components/PhoneCaptureModal";
import { formatTitleCase } from "./utils/formatters";
import {
	buildOrganizationDomainGroups,
	findOrganizationDomainKey,
	getOrganizationDomainLabel,
} from "./utils/organizationDomains";
import {
	getCustomerTimeline,
	getCustomers,
	getKpis,
	logCustomerAction,
	logSegmentAction,
	recalculateRfm,
	getSegmentMessageTemplates,
	resetSegmentMessageTemplate,
	getSuggestiveActionsConfig,
	createCustomSuggestiveAction,
	updateCustomSuggestiveAction,
	toggleSuggestiveActionVisibility,
	toggleSuggestiveActionsBulkVisibility,
	deleteCustomSuggestiveAction,
	resetSegmentSuggestiveActions,
	getRevenueTrend,
	getSegmentSummary,
	getTopProducts,
	getActionsContext,
	generateCustomerPortfolioReport,
	getWhatsAppEmbeddedSignupConfig,
	getWhatsAppConnection,
	completeWhatsAppEmbeddedSignup,
	disconnectWhatsAppConnection,
	getWhatsAppTemplates,
	getWhatsAppAnalytics,
	sendWhatsAppSegmentCampaign,
	createWhatsAppTemplateRequest,
} from "./api/dashboardApi";
import { createOrganization, listOrganizations, updateOrganizationReportContact } from "./api/etlApi";
import {
	getAuthIdToken,
	getAuthRefreshToken,
	getAuthToken,
	getExploreTrialId,
	getEmbeddedOrgId,
	isEmbeddedSession,
	refreshAuthSession,
	subscribeToAuthExpired,
	clearExploreTrialId,
	setEmbeddedSession,
} from "./api/client";
import StartTrial from "./pages/StartTrial";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import {
	changeCurrentUserPassword,
	getAuthMethods,
	getCurrentUserProfile,
	getOAuthLoginUrl,
	getOAuthLogoutUrl,
	loginWithPassword,
	logoutSession,
	silentLogoutSession,
	exchangeOAuthCode,
	saveTrialPhone,
} from "./api/authApi";

const EMPTY_KPIS = {
	total_customers: 0,
	total_revenue: 0,
	average_order_value: 0,
	average_purchase_frequency: 0,
};

const EMPTY_SEGMENT_SUMMARY = {
	insights: [],
	segments: [],
};

const EMPTY_REVENUE_TREND = { series: [] };
const EMPTY_TOP_PRODUCTS = { items: [] };
const EMPTY_TIMELINE = { visits: [], spend_trend: [], actions: [] };
const DEFAULT_TERMINOLOGY = {
	customer_label: "Customer",
	customer_plural_label: "Customers",
	activity_label: "Visit",
	activity_plural_label: "Visits",
	activity_count_label: "Visits",
	last_activity_label: "Last Visit",
	last_activity_short_label: "Last Visit",
	activity_frequency_label: "Visit Frequency",
	avg_activity_frequency_label: "Avg Visit Frequency",
	avg_activity_frequency_sub_label: "all-time avg visits per customer",
	transaction_label: "Transaction",
	transaction_plural_label: "Transactions",
	revenue_label: "Revenue",
	spend_label: "Spend",
	total_spend_label: "Total Spend",
	top_items_title: "Top Product Categories by Quantity",
	top_items_series_label: "Quantity",
	item_label: "Product",
	item_plural_label: "Products",
	item_category_label: "Product Category",
	item_unit_label: "Quantity",
};

function loadFacebookSdk() {
	if (typeof window === "undefined") {
		return Promise.reject(new Error("Facebook SDK is only available in the browser."));
	}
	if (window.FB) {
		return Promise.resolve(window.FB);
	}

	return new Promise((resolve, reject) => {
		const existing = document.getElementById("facebook-jssdk");
		if (existing) {
			existing.addEventListener("load", () => resolve(window.FB), { once: true });
			existing.addEventListener("error", () => reject(new Error("Failed to load Facebook SDK.")), { once: true });
			return;
		}

		const script = document.createElement("script");
		script.id = "facebook-jssdk";
		script.src = "https://connect.facebook.net/en_US/sdk.js";
		script.async = true;
		script.defer = true;
		script.onload = () => resolve(window.FB);
		script.onerror = () => reject(new Error("Failed to load Facebook SDK."));
		document.body.appendChild(script);
	});
}

function extractWhatsAppSignupIds(payload) {
	const data = payload?.data || payload?.payload || payload || {};
	const findNestedValue = (value, keys) => {
		if (!value) {
			return null;
		}
		if (Array.isArray(value)) {
			for (const item of value) {
				const nested = findNestedValue(item, keys);
				if (nested) {
					return nested;
				}
			}
			return null;
		}
		if (typeof value === "object") {
			for (const key of keys) {
				const direct = value?.[key];
				if (direct) {
					return direct;
				}
			}
			for (const nestedValue of Object.values(value)) {
				const nested = findNestedValue(nestedValue, keys);
				if (nested) {
					return nested;
				}
			}
		}
		return null;
	};
	const findKeyedObjectId = (value, keyMarkers) => {
		if (!value) {
			return null;
		}
		if (Array.isArray(value)) {
			for (const item of value) {
				const nested = findKeyedObjectId(item, keyMarkers);
				if (nested) {
					return nested;
				}
			}
			return null;
		}
		if (typeof value === "object") {
			for (const [key, item] of Object.entries(value)) {
				if (keyMarkers.some((marker) => String(key).toLowerCase().includes(marker))) {
					if (item && typeof item === "object" && !Array.isArray(item)) {
						const found = item.id || item.asset_id;
						if (found) return found;
					}
					if (item && typeof item !== "object") return item;
				}
				const nested = findKeyedObjectId(item, keyMarkers);
				if (nested) {
					return nested;
				}
			}
		}
		return null;
	};
	const findTypedAssetId = (value, typeMarkers) => {
		if (!value) {
			return null;
		}
		if (Array.isArray(value)) {
			for (const item of value) {
				const nested = findTypedAssetId(item, typeMarkers);
				if (nested) {
					return nested;
				}
			}
			return null;
		}
		if (typeof value === "object") {
			const assetId = value.asset_id || value.id;
			const descriptor = ["type", "asset_type", "assetType", "product", "name", "code"]
				.map((key) => value?.[key] || "")
				.join(" ")
				.toLowerCase();
			if (assetId && typeMarkers.some((marker) => descriptor.includes(marker))) {
				return assetId;
			}
			for (const nestedValue of Object.values(value)) {
				const nested = findTypedAssetId(nestedValue, typeMarkers);
				if (nested) {
					return nested;
				}
			}
		}
		return null;
	};
	const wabaId =
		findNestedValue(data, ["waba_id", "whatsapp_business_account_id", "whatsappBusinessAccountId", "wabaId"]) ||
		findKeyedObjectId(data, ["waba", "whatsapp_business_account"]) ||
		findTypedAssetId(data, ["waba", "whatsapp_business_account"]) ||
		null;
	return {
		waba_id: wabaId,
		phone_number_id:
			findNestedValue(data, ["phone_number_id", "phoneNumberId", "whatsapp_phone_number_id", "phone_id"]) || null,
		display_phone_number:
			findNestedValue(data, [
				"display_phone_number",
				"displayPhoneNumber",
				"phone_number",
				"phoneNumber",
				"business_phone_number",
				"businessPhoneNumber",
				"whatsapp_phone_number",
				"whatsappPhoneNumber",
			]) || null,
		business_id:
			findNestedValue(data, ["business_id", "businessId", "facebook_business_id", "business_account_id"]) || null,
	};
}

function formatWhatsAppConnectionSummary(connection) {
	const wabaId = String(connection?.waba_id || "").trim();
	const displayPhoneNumber = String(connection?.display_phone_number || connection?.phone_number || "").trim();
	const phoneNumberId = String(connection?.phone_number_id || "").trim();
	const details = [
		wabaId ? `WABA ID ${wabaId}` : null,
		displayPhoneNumber ? `Phone ${displayPhoneNumber}` : phoneNumberId ? `Phone ID ${phoneNumberId}` : null,
	].filter(Boolean);
	return details.join(" | ");
}
const PKCE_VERIFIER_KEY = "rfm_oauth_pkce_verifier";
const LAST_LOGOUT_EMAIL_KEY = "rfm_last_logout_email";
const ADD_ACCOUNT_AFTER_LOGOUT_KEY = "rfm_oauth_add_account_after_logout";
const ADD_ACCOUNT_RETURN_PATH_KEY = "rfm_oauth_add_account_return_path";
const OAUTH_UNAUTHORIZED_ERROR_KEY = "rfm_oauth_unauthorized_error";
const OAUTH_EXPLORE_MISMATCH_ERROR_KEY = "rfm_oauth_explore_mismatch_error";
const EXPLORE_LOGIN_ERROR_KEY = "rfm_explore_login_error";
const FORCE_ACCOUNT_CHOOSER_ONCE_KEY = "rfm_force_account_chooser_once";
const OAUTH_EXCHANGE_LOCK_KEY = "rfm_oauth_exchange_lock";
const OAUTH_LAST_CODE_KEY = "rfm_oauth_last_code";
const TRIAL_PREFILL_PHONE_KEY = "rfm_trial_prefill_phone";
const TRIAL_PREFILL_NAME_KEY = "rfm_trial_prefill_name";
const DEMO_ORG_ID = "5cf12c0f-460c-4191-b0bb-ae83f2b62024";
const DEMO_ORG_NAME = "Ecommerce - Test Org";
const SAVED_ACCOUNTS_KEY = "rfm_saved_accounts";
const SELECTED_ORG_STORAGE_PREFIX = "rfm_selected_org";
const LAST_SELECTED_ORG_KEY = "rfm_last_selected_org";
const DEFAULT_ORG_ID = String(process.env.REACT_APP_DEFAULT_ORG_ID || "").trim();
const REPORT_LANGUAGE = "en";
const REPORT_LANGUAGE_LABEL = "English";
const EMBED_PARENT_ORIGINS = String(process.env.REACT_APP_EMBED_PARENT_ORIGINS || "")
	.split(",")
	.map((item) => item.trim().replace(/\/$/, ""))
	.filter(Boolean);
const MAX_SAVED_ACCOUNTS = 8;
const LOGIN_HERO_SLIDES = [
	{
		kicker: "KORVRFM",
		title: "RFM Analytics Workspace",
		description:
			"See customer value, purchase behavior, and growth opportunities in one clear business view.",
		points: ["RFM Scoring", "Customer Segments", "Lifecycle Insight"],
		image: "/login-slides-rfm/rfm-analytics.svg",
	},
	{
		kicker: "KORVRFM",
		title: "Actionable Segment Intelligence",
		description:
			"Track high-value and at-risk segments, then launch focused actions to improve retention.",
		points: ["Segment Summary", "Targeted Actions", "Message Templates"],
		image: "/login-slides-rfm/rfm-segments.svg",
	},
	{
		kicker: "KORVRFM",
		title: "Revenue Trend Visibility",
		description:
			"Understand trend movement, top products, and customer timelines to guide smarter campaigns.",
		points: ["Revenue Trend", "Top Products", "Customer Timeline"],
		image: "/login-slides-rfm/rfm-revenue.svg",
	},
	{
		kicker: "KORVRFM",
		title: "Built For Any Business Domain",
		description:
			"Use one secure platform across ecommerce, services, SaaS, education, healthcare, and more.",
		points: ["Secure Access", "Org-Based Data", "Unified Dashboard"],
		image: "/login-slides-rfm/rfm-actions.svg",
	},
];

function normalizeAccountEmail(value) {
	return String(value || "").trim().toLowerCase();
}

function decodeJwtPayload(token) {
	try {
		const payload = String(token || "").split(".")[1] || "";
		if (!payload) {
			return {};
		}
		const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
		const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
		return JSON.parse(window.atob(padded));
	} catch (_error) {
		return {};
	}
}

function getEmailFromStoredToken() {
	if (typeof window === "undefined") {
		return "";
	}
	const claims = decodeJwtPayload(getAuthToken()) || {};
	return normalizeAccountEmail(claims.email || claims.preferred_username || "");
}

function getSelectedOrgStorageKey(email) {
	const normalizedEmail = normalizeAccountEmail(email);
	if (!normalizedEmail) {
		return "";
	}
	return `${SELECTED_ORG_STORAGE_PREFIX}:${normalizedEmail}`;
}

function readRememberedOrgId(email) {
	if (typeof window === "undefined") {
		return "";
	}
	const storageKey = getSelectedOrgStorageKey(email);
	if (!storageKey) {
		return "";
	}
	return String(window.localStorage.getItem(storageKey) || "").trim();
}

function readLastSelectedOrgId() {
	if (typeof window === "undefined") {
		return "";
	}
	return String(window.localStorage.getItem(LAST_SELECTED_ORG_KEY) || "").trim();
}

function writeRememberedOrgId(email, orgId) {
	if (typeof window === "undefined") {
		return;
	}
	const storageKey = getSelectedOrgStorageKey(email);
	const normalizedOrgId = String(orgId || "").trim();
	if (!normalizedOrgId) {
		if (storageKey) {
			window.localStorage.removeItem(storageKey);
		}
		window.localStorage.removeItem(LAST_SELECTED_ORG_KEY);
		return;
	}
	if (storageKey) {
		window.localStorage.setItem(storageKey, normalizedOrgId);
	}
	window.localStorage.setItem(LAST_SELECTED_ORG_KEY, normalizedOrgId);
}

function normalizeOrgId(value) {
	return String(value || "").trim();
}

function isAllowedEmbedOrigin(origin) {
	if (!EMBED_PARENT_ORIGINS.length) {
		return true;
	}
	const normalizedOrigin = String(origin || "").trim().replace(/\/$/, "");
	return EMBED_PARENT_ORIGINS.includes(normalizedOrigin);
}

function isEmbeddedRequestUrl() {
	if (typeof window === "undefined") {
		return false;
	}
	const params = new URLSearchParams(window.location.search || "");
	return params.get("embedded") === "1";
}

function normalizeOrganization(org) {
	if (!org || typeof org !== "object") {
		return null;
	}
	const orgId = normalizeOrgId(org.org_id);
	if (!orgId) {
		return null;
	}
	return {
		...org,
		org_id: orgId,
		domain_hint: String(org.domain_hint || org.domainHint || "").trim(),
	};
}

function normalizeOrganizations(orgs) {
	if (!Array.isArray(orgs)) {
		return [];
	}
	return orgs.map(normalizeOrganization).filter(Boolean);
}

function readSavedAccounts() {
	if (typeof window === "undefined") {
		return [];
	}
	try {
		const raw = window.localStorage.getItem(SAVED_ACCOUNTS_KEY);
		if (!raw) {
			return [];
		}
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) {
			return [];
		}
		return parsed
			.filter((item) => normalizeAccountEmail(item?.email) && String(item?.access_token || "").trim())
			.map((item) => ({
				email: normalizeAccountEmail(item.email),
				role: String(item.role || "user"),
				auth_provider: String(item.auth_provider || "oauth"),
				access_token: String(item.access_token || ""),
				id_token: String(item.id_token || ""),
				refresh_token: String(item.refresh_token || ""),
				last_used_at: String(item.last_used_at || ""),
			}))
			.sort((a, b) => String(b.last_used_at).localeCompare(String(a.last_used_at)));
	} catch {
		return [];
	}
}

function writeSavedAccounts(accounts) {
	if (typeof window === "undefined") {
		return;
	}
	window.localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(accounts));
}

function upsertSavedAccount(payload) {
	if (typeof window === "undefined") {
		return;
	}
	const email = normalizeAccountEmail(payload?.email);
	if (!email) {
		return;
	}

	const accessToken = String(getAuthToken() || "").trim();
	if (!accessToken) {
		return;
	}

	const next = [
		{
			email,
			role: String(payload?.role || "user"),
			auth_provider: String(payload?.auth_provider || payload?.authProvider || "oauth"),
			access_token: accessToken,
			id_token: String(getAuthIdToken() || ""),
			refresh_token: String(getAuthRefreshToken() || ""),
			last_used_at: new Date().toISOString(),
		},
		...readSavedAccounts().filter((item) => item.email !== email),
	].slice(0, MAX_SAVED_ACCOUNTS);

	writeSavedAccounts(next);
}

function base64UrlEncode(bytes) {
	const binary = Array.from(bytes)
		.map((byte) => String.fromCharCode(byte))
		.join("");
	return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

async function generatePkcePair() {
	const randomBytes = new Uint8Array(32);
	window.crypto.getRandomValues(randomBytes);
	const verifier = base64UrlEncode(randomBytes);
	const digest = await window.crypto.subtle.digest(
		"SHA-256",
		new TextEncoder().encode(verifier)
	);
	const challenge = base64UrlEncode(new Uint8Array(digest));
	return { verifier, challenge };
}

function ProtectedRoute({ isAuthenticated, isAuthBootstrapping = false, allowPendingEmbedded = false, children }) {
	if (isAuthBootstrapping) {
		return <AppLoader message="Restoring your session..." />;
	}
	if (!isAuthenticated) {
		if (allowPendingEmbedded) {
			return <AppLoader title="Waiting For Access" message="Waiting for the main application to open RFM analysis..." />;
		}
		return <Navigate to="/" replace />;
	}
	return children;
}

function AdminRoute({ isAuthenticated, isAuthBootstrapping = false, isAdmin, children }) {
	if (isAuthBootstrapping) {
		return <AppLoader message="Restoring your session..." />;
	}
	if (!isAuthenticated) {
		return <Navigate to="/" replace />;
	}
	if (!isAdmin) {
		return <Navigate to="/onboarding" replace />;
	}
	return children;
}

function ClientManagerRoute({ isAuthenticated, isAuthBootstrapping = false, canManageEmployees, children }) {
	if (isAuthBootstrapping) {
		return <AppLoader message="Restoring your session..." />;
	}
	if (!isAuthenticated) {
		return <Navigate to="/" replace />;
	}
	if (!canManageEmployees) {
		return <Navigate to="/onboarding" replace />;
	}
	return children;
}

export default function App() {
	const location = useLocation();
	const navigate = useNavigate();
	const isEmbeddedMode = useMemo(() => {
		const params = new URLSearchParams(location.search || "");
		return params.get("embedded") === "1" || isEmbeddedSession();
	}, [location.search]);
	const [isAuthenticated, setIsAuthenticated] = useState(() => {
		if (isEmbeddedRequestUrl() && !isEmbeddedSession()) {
			return false;
		}
		return Boolean(getAuthToken());
	});
	const [loginEmail, setLoginEmail] = useState("");
	const [loginPassword, setLoginPassword] = useState("");
	const [loginError, setLoginError] = useState("");
	const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
	const [isOAuthSubmitting, setIsOAuthSubmitting] = useState(false);
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
	const [currentPasswordInput, setCurrentPasswordInput] = useState("");
	const [newPasswordInput, setNewPasswordInput] = useState("");
	const [confirmNewPasswordInput, setConfirmNewPasswordInput] = useState("");
	const [isPasswordChangeSubmitting, setIsPasswordChangeSubmitting] = useState(false);
	const [authProfile, setAuthProfile] = useState(null);
	const [isAuthBootstrapping, setIsAuthBootstrapping] = useState(() => {
		if (isEmbeddedRequestUrl() && !isEmbeddedSession()) {
			return false;
		}
		return Boolean(getAuthToken() || getAuthRefreshToken());
	});
	const [isLoading, setIsLoading] = useState(true);
	const [authMethods, setAuthMethods] = useState({
		password_enabled: true,
		oauth_enabled: true,
		show_use_another_google_account: true,
	});
	const [oauthRetryAvailable, setOauthRetryAvailable] = useState(false);
	const [isAuthMethodsLoading, setIsAuthMethodsLoading] = useState(true);
	const [isTrialStatusLoaded, setIsTrialStatusLoaded] = useState(false);
	const [isTrialAccount, setIsTrialAccount] = useState(false);
	const [isAnalysisDataReady, setIsAnalysisDataReady] = useState(false);
	const [showPhoneCaptureModal, setShowPhoneCaptureModal] = useState(false);
	const [isPhoneSubmitting, setIsPhoneSubmitting] = useState(false);
	const [activeHeroSlide, setActiveHeroSlide] = useState(0);
	const [customers, setCustomers] = useState([]);
	const [selectedSegments, setSelectedSegments] = useState([]);
	const [selectedCustomerId, setSelectedCustomerId] = useState(null);
	const [selectedDashboardMetric, setSelectedDashboardMetric] = useState("clients");

	const [sortKey, setSortKey] = useState("composite_score");
	const [sortDirection, setSortDirection] = useState(-1);

	const [kpis, setKpis] = useState(EMPTY_KPIS);
	const [segmentSummary, setSegmentSummary] = useState(EMPTY_SEGMENT_SUMMARY);
	const [revenueTrend, setRevenueTrend] = useState(EMPTY_REVENUE_TREND);
	const [topProducts, setTopProducts] = useState(EMPTY_TOP_PRODUCTS);

	const [toast, setToast] = useState({ message: "", type: "info" });
	const [segmentActionContext, setSegmentActionContext] = useState(null);
	const [timeline, setTimeline] = useState(EMPTY_TIMELINE);
	const [isTimelineLoading, setIsTimelineLoading] = useState(false);
	const [organizationName, setOrganizationName] = useState("Your Shop");
	const [organizations, setOrganizations] = useState([]);
	const [isOrganizationsLoading, setIsOrganizationsLoading] = useState(true);
	const [selectedOrgId, setSelectedOrgId] = useState("");
	const [newOrganizationName, setNewOrganizationName] = useState("");
	const [requestOrganizationName, setRequestOrganizationName] = useState("");
	const [isSubmittingOrgRequest, setIsSubmittingOrgRequest] = useState(false);
	const [pendingNewOrgName, setPendingNewOrgName] = useState("");
	const [newlyCreatedOrgIds, setNewlyCreatedOrgIds] = useState([]);
	const [onboardingPopup, setOnboardingPopup] = useState(null);
	const [onboardingDomainKey, setOnboardingDomainKey] = useState("");
	const [orgReportUrl, setOrgReportUrl] = useState("");
	const [orgReportHtml, setOrgReportHtml] = useState("");
	const [isGeneratingOrgReport, setIsGeneratingOrgReport] = useState(false);
	const [isReportContactOpen, setIsReportContactOpen] = useState(false);
	const [reportContactForm, setReportContactForm] = useState({
		contact_person_name: "",
		contact_phone: "",
		contact_address: "",
	});
	const [whatsappConnection, setWhatsappConnection] = useState(null);
	const [whatsappTemplates, setWhatsappTemplates] = useState([]);
	const [whatsappTemplateLoadError, setWhatsappTemplateLoadError] = useState("");
	const [whatsappAnalytics, setWhatsappAnalytics] = useState(null);
	const [isWhatsAppConnecting, setIsWhatsAppConnecting] = useState(false);
	const [isWhatsAppDisconnecting, setIsWhatsAppDisconnecting] = useState(false);
	const [segmentMessageTemplates, setSegmentMessageTemplates] = useState({});
	const [suggestiveActionsConfig, setSuggestiveActionsConfig] = useState({
		custom_actions: [],
		hidden_action_keys: [],
		deleted_action_keys: [],
		usage_count: {},
	});
	const selectedCustomerScrollRestoreRef = useRef(null);
	const lastKnownViewportScrollRef = useRef(0);
	const previousSelectedCustomerIdRef = useRef(selectedCustomerId);
	const oauthExchangeInFlightRef = useRef(false);
	const lastHandledOAuthCodeRef = useRef("");
	const isOAuthSubmittingRef = useRef(false);
	const sessionExpiredHandledRef = useRef(false);
	const orgReportUrlRef = useRef("");
	const orgReportFrameRef = useRef(null);

	const selectedCustomer = useMemo(() => {
		return customers.find((item) => item.customer_id === selectedCustomerId) || null;
	}, [customers, selectedCustomerId]);

	useEffect(() => {
		orgReportUrlRef.current = orgReportUrl;
	}, [orgReportUrl]);

	useEffect(() => {
		return () => {
			if (orgReportUrlRef.current) {
				URL.revokeObjectURL(orgReportUrlRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		const captureScroll = () => {
			lastKnownViewportScrollRef.current =
				window.scrollY ||
				window.pageYOffset ||
				document.documentElement?.scrollTop ||
				0;
		};

		captureScroll();
		window.addEventListener("scroll", captureScroll, { passive: true });
		return () => window.removeEventListener("scroll", captureScroll);
	}, []);

	const handleSelectCustomer = useCallback((nextCustomerId) => {
		if (!nextCustomerId && typeof window !== "undefined") {
			selectedCustomerScrollRestoreRef.current = lastKnownViewportScrollRef.current;
		}
		if (nextCustomerId) {
			selectedCustomerScrollRestoreRef.current = null;
		}
		setSelectedCustomerId(nextCustomerId);
	}, []);

	useLayoutEffect(() => {
		if (typeof window === "undefined") {
			previousSelectedCustomerIdRef.current = selectedCustomerId;
			return;
		}

		const hadSelectedCustomer = Boolean(previousSelectedCustomerIdRef.current);
		const isSelectionCleared = hadSelectedCustomer && !selectedCustomerId;
		if (!isSelectionCleared) {
			previousSelectedCustomerIdRef.current = selectedCustomerId;
			selectedCustomerScrollRestoreRef.current = null;
			return;
		}

		const currentScroll =
			window.scrollY ||
			window.pageYOffset ||
			document.documentElement?.scrollTop ||
			0;
		const scrollPosition =
			selectedCustomerScrollRestoreRef.current ?? lastKnownViewportScrollRef.current ?? currentScroll;
		selectedCustomerScrollRestoreRef.current = null;
		window.scrollTo({ top: scrollPosition, left: 0, behavior: "auto" });
		previousSelectedCustomerIdRef.current = selectedCustomerId;
	}, [selectedCustomerId]);

	const selectedOrg = useMemo(() => {
		return organizations.find((item) => item.org_id === selectedOrgId) || null;
	}, [organizations, selectedOrgId]);

	const selectedOrgName = useMemo(() => {
		return formatTitleCase(selectedOrg?.name || "");
	}, [selectedOrg]);

	const selectedOrgTerminology = useMemo(() => {
		return {
			...DEFAULT_TERMINOLOGY,
			...(selectedOrg?.terminology || {}),
		};
	}, [selectedOrg]);

	const hasOrganizations = useMemo(() => organizations.length > 0, [organizations.length]);
	const hasSingleOrganization = useMemo(() => organizations.length === 1, [organizations.length]);
	const organizationDomainGroups = useMemo(() => buildOrganizationDomainGroups(organizations), [organizations]);
	const onboardingDomainExists = organizationDomainGroups.some((group) => group.key === onboardingDomainKey);
	const activeOnboardingDomainKey =
		(onboardingDomainExists ? onboardingDomainKey : "") ||
		findOrganizationDomainKey(organizations, selectedOrgId) ||
		organizationDomainGroups[0]?.key ||
		"";
	const activeOnboardingDomain =
		organizationDomainGroups.find((group) => group.key === activeOnboardingDomainKey) ||
		organizationDomainGroups[0] ||
		null;
	const onboardingDomainOrganizations = activeOnboardingDomain?.organizations || [];

	const selectedOrgIsNew = useMemo(() => {
		return Boolean(selectedOrgId) && newlyCreatedOrgIds.includes(selectedOrgId);
	}, [newlyCreatedOrgIds, selectedOrgId]);

	const canGoToAnalysis = useMemo(() => {
		if (pendingNewOrgName.trim()) {
			return false;
		}
		if (!selectedOrgId) {
			return false;
		}
		if (!selectedOrgIsNew) {
			return true;
		}
		return customers.length > 0;
	}, [customers.length, pendingNewOrgName, selectedOrgId, selectedOrgIsNew]);

	const isAdmin = useMemo(() => authProfile?.role === "admin", [authProfile]);
	const canManageEmployees = useMemo(() => Boolean(authProfile?.can_manage_employees), [authProfile]);
	const canCreateOrgWithoutApproval = useMemo(
		() => Boolean(authProfile?.can_create_org_without_approval),
		[authProfile?.can_create_org_without_approval]
	);
	const isRoleResolved = useMemo(
		() => !isAuthBootstrapping && Boolean(authProfile),
		[authProfile, isAuthBootstrapping]
	);
	const isClientManager = useMemo(() => canManageEmployees && !isAdmin, [canManageEmployees, isAdmin]);
	const isTrialRestricted = useMemo(() => isTrialStatusLoaded && isTrialAccount, [isTrialAccount, isTrialStatusLoaded]);
	const canUseWhatsApp = useMemo(
		() => !isEmbeddedMode && isAuthenticated && isTrialStatusLoaded && !passwordChangeRequired,
		[isAuthenticated, isEmbeddedMode, isTrialStatusLoaded, passwordChangeRequired]
	);
	const canStartFreshOrganization = useMemo(
		() => !isTrialRestricted && (isAdmin || (isClientManager && canCreateOrgWithoutApproval)),
		[canCreateOrgWithoutApproval, isAdmin, isClientManager, isTrialRestricted]
	);
	const canRequestOrganization = useMemo(
		() => !isTrialRestricted && isClientManager && !canCreateOrgWithoutApproval,
		[canCreateOrgWithoutApproval, isClientManager, isTrialRestricted]
	);
	const profileDisplayName = useMemo(() => {
		const fullName = String(authProfile?.full_name || "").trim();
		if (fullName) {
			return formatTitleCase(fullName);
		}
		return normalizeAccountEmail(authProfile?.email) || getEmailFromStoredToken();
	}, [authProfile?.email, authProfile?.full_name]);
	const currentAccountEmail = useMemo(
		() => normalizeAccountEmail(authProfile?.email) || getEmailFromStoredToken(),
		[authProfile?.email]
	);
	const currentHeroSlide = useMemo(() => {
		return LOGIN_HERO_SLIDES[activeHeroSlide] || LOGIN_HERO_SLIDES[0];
	}, [activeHeroSlide]);

	const resetWorkspaceState = useCallback(() => {
		setSegmentActionContext(null);
		setSelectedCustomerId(null);
		setCustomers([]);
		setSelectedSegments([]);
		setTimeline(EMPTY_TIMELINE);
		setKpis(EMPTY_KPIS);
		setSegmentSummary(EMPTY_SEGMENT_SUMMARY);
		setRevenueTrend(EMPTY_REVENUE_TREND);
		setTopProducts(EMPTY_TOP_PRODUCTS);
		setOrganizations([]);
		setSelectedOrgId("");
		setWhatsappConnection(null);
		setWhatsappTemplates([]);
		setWhatsappTemplateLoadError("");
		setWhatsappAnalytics(null);
		setIsAnalysisDataReady(false);
		setNewOrganizationName("");
		setPendingNewOrgName("");
		setNewlyCreatedOrgIds([]);
		setOnboardingPopup(null);
	}, []);

	const clearSessionState = useCallback(({ preserveExploreTrial = false } = {}) => {
		logoutSession();
		oauthExchangeInFlightRef.current = false;
		lastHandledOAuthCodeRef.current = "";
		isOAuthSubmittingRef.current = false;
		setIsOAuthSubmitting(false);
		setIsAuthenticated(false);
		setIsAuthBootstrapping(false);
		setAuthProfile(null);
		setIsTrialAccount(false);
		setIsTrialStatusLoaded(false);
		setPasswordChangeRequired(false);
		setLoginEmail("");
		setLoginPassword("");
		setCurrentPasswordInput("");
		setNewPasswordInput("");
		setConfirmNewPasswordInput("");
		resetWorkspaceState();
		if (typeof window !== "undefined") {
			window.sessionStorage.removeItem("rfm_oauth_state");
			window.sessionStorage.removeItem("rfm_oauth_redirect_uri");
			window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
			if (!preserveExploreTrial) {
				clearExploreTrialId();
			}
			window.sessionStorage.removeItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY);
			window.sessionStorage.removeItem(ADD_ACCOUNT_RETURN_PATH_KEY);
			window.sessionStorage.removeItem(OAUTH_UNAUTHORIZED_ERROR_KEY);
			window.sessionStorage.removeItem(OAUTH_EXPLORE_MISMATCH_ERROR_KEY);
			window.sessionStorage.removeItem(OAUTH_EXCHANGE_LOCK_KEY);
			window.sessionStorage.removeItem(OAUTH_LAST_CODE_KEY);
			const url = new URL(window.location.href);
			url.searchParams.delete("code");
			url.searchParams.delete("state");
			url.searchParams.delete("session_state");
			url.searchParams.delete("iss");
			url.searchParams.delete("error");
			const nextUrl = `${url.pathname}${url.search}${url.hash}`;
			const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
			if (nextUrl !== currentUrl) {
				try {
					window.history.replaceState({}, document.title, nextUrl);
				} catch {
					// Browsers can reject history updates when several redirects happen quickly.
				}
			}
		}
	}, [resetWorkspaceState]);

	const handleSessionExpired = useCallback((message = "Session expired. Please login again.") => {
		if (sessionExpiredHandledRef.current) {
			return;
		}
		sessionExpiredHandledRef.current = true;
		if (isEmbeddedMode) {
			clearSessionState();
			setLoginError(message);
			setIsLoading(false);
			if (location.pathname !== "/analysis") {
				navigate("/analysis?embedded=1", { replace: true });
			}
			return;
		}
		const hadExploreTrial = Boolean(getExploreTrialId());
		clearSessionState({ preserveExploreTrial: hadExploreTrial });
		setLoginError(message);
		const destination = hadExploreTrial ? "/explore" : "/";
		if (location.pathname !== destination) {
			navigate(destination, { replace: true });
		}
	}, [clearSessionState, isEmbeddedMode, location.pathname, navigate]);

	const showToast = useCallback((message, type = "info") => {
		setToast({ message, type });
	}, []);

	useEffect(() => {
		if (!isEmbeddedMode || typeof window === "undefined") {
			return undefined;
		}

		setIsAuthBootstrapping(false);
		setPasswordChangeRequired(false);
		setIsTrialAccount(false);
		setIsTrialStatusLoaded(true);
		if (location.pathname !== "/analysis") {
			navigate("/analysis?embedded=1", { replace: true });
		}

		const hydrateEmbeddedShell = (orgId, orgName = "Embedded Organization") => {
			setAuthProfile({
				email: "",
				full_name: "Embedded Viewer",
				role: "user",
				auth_provider: "embedded",
				password_change_required: false,
				can_create_org_without_approval: false,
				can_manage_employees: false,
				show_gemini_signature: false,
				organizations: [{ org_id: orgId, name: orgName, access_status: "approved" }],
			});
			setOrganizations([{ org_id: orgId, name: orgName, access_status: "approved" }]);
			setSelectedOrgId(orgId);
			setPasswordChangeRequired(false);
			setIsTrialAccount(false);
			setIsTrialStatusLoaded(true);
			setIsOrganizationsLoading(false);
			setIsAuthenticated(true);
			setIsAuthBootstrapping(false);
			sessionExpiredHandledRef.current = false;
		};

		const existingEmbeddedOrgId = normalizeOrgId(getEmbeddedOrgId());
		if (String(getAuthToken() || "").trim() && existingEmbeddedOrgId) {
			hydrateEmbeddedShell(existingEmbeddedOrgId);
		} else {
			setIsAuthenticated(false);
			setIsOrganizationsLoading(false);
			setIsAnalysisDataReady(false);
			setIsLoading(false);
		}

		const onEmbeddedAuthMessage = (event) => {
			if (!isAllowedEmbedOrigin(event.origin)) {
				return;
			}
			const payload = event.data || {};
			if (!payload || typeof payload !== "object") {
				return;
			}
			if (!["RFM_EMBED_AUTH", "RFM_EMBED_REFRESH"].includes(payload.type)) {
				return;
			}

			const accessToken = String(payload.accessToken || payload.access_token || "").trim();
			const refreshToken = String(payload.refreshToken || payload.refresh_token || "").trim();
			const orgId = normalizeOrgId(payload.orgId || payload.org_id);
			if (!accessToken || !orgId) {
				setLoginError("Embedded RFM access requires an access token and org id.");
				setIsLoading(false);
				return;
			}

			setEmbeddedSession({ accessToken, refreshToken, orgId });
			hydrateEmbeddedShell(orgId, payload.orgName || payload.org_name || "Embedded Organization");
			setIsAnalysisDataReady(false);
		};

		window.addEventListener("message", onEmbeddedAuthMessage);
		window.parent?.postMessage({ type: "RFM_EMBED_READY" }, "*");

		return () => window.removeEventListener("message", onEmbeddedAuthMessage);
	}, [isEmbeddedMode, location.pathname, navigate]);

	const formatAuthError = useCallback((error, fallbackMessage) => {
		const status = Number(error?.response?.status || 0);
		const detail =
			error?.response?.data?.detail ||
			error?.response?.data?.message ||
			error?.message ||
			fallbackMessage;

		if (status > 0) {
			return `[${status}] ${detail}`;
		}
		return detail || fallbackMessage;
	}, []);

	const loadOrganizations = useCallback(async () => {
		if (isEmbeddedMode) {
			const embeddedOrgId = normalizeOrgId(getEmbeddedOrgId() || selectedOrgId);
			if (embeddedOrgId) {
				setOrganizations((current) => current.length ? current : [{ org_id: embeddedOrgId, name: "Embedded Organization", access_status: "approved" }]);
				setSelectedOrgId(embeddedOrgId);
			}
			setIsOrganizationsLoading(false);
			return embeddedOrgId ? [{ org_id: embeddedOrgId, name: "Embedded Organization", access_status: "approved" }] : [];
		}

		setIsOrganizationsLoading(true);

		try {
			const orgs = normalizeOrganizations(await listOrganizations());
			setOrganizations(orgs);
				setSelectedOrgId((current) => {
					const normalizedCurrent = normalizeOrgId(current);
					if (normalizedCurrent && orgs.some((org) => org.org_id === normalizedCurrent)) {
						return normalizedCurrent;
					}
					const rememberedOrgId = normalizeOrgId(readRememberedOrgId(currentAccountEmail));
					if (rememberedOrgId && orgs.some((org) => org.org_id === rememberedOrgId)) {
						return rememberedOrgId;
					}
					const lastSelectedOrgId = normalizeOrgId(readLastSelectedOrgId());
					if (lastSelectedOrgId && orgs.some((org) => org.org_id === lastSelectedOrgId)) {
						return lastSelectedOrgId;
					}
					const assignedOrgId = orgs.find((org) => !org.is_default)?.org_id || "";
					if (assignedOrgId) {
						return assignedOrgId;
					}
				const backendDefaultOrgId = orgs.find((org) => org.is_default)?.org_id || "";
				if (backendDefaultOrgId) {
					return backendDefaultOrgId;
				}
				if (DEFAULT_ORG_ID && orgs.some((org) => org.org_id === DEFAULT_ORG_ID)) {
					return DEFAULT_ORG_ID;
				}
				return (orgs[0] && orgs[0].org_id) || "";
			});
			return orgs;
		} catch (error) {
			if (getExploreTrialId()) {
				const demoOrgs = [{ org_id: DEMO_ORG_ID, name: DEMO_ORG_NAME, access_status: "approved" }];
				setOrganizations(demoOrgs);
				setSelectedOrgId(DEMO_ORG_ID);
				showToast(`Using fallback demo organization: ${error.message}`, "warning");
				return demoOrgs;
			}
			setOrganizations([]);
			setSelectedOrgId("");
			showToast(`Failed to load businesses: ${error.message}`, "error");
			return [];
		} finally {
			setIsOrganizationsLoading(false);
		}
	}, [currentAccountEmail, isEmbeddedMode, selectedOrgId, showToast]);

	const loadCustomers = useCallback(async () => {
		if (isOrganizationsLoading) {
			return;
		}

		if (location.pathname === "/analysis") {
			return;
		}

		if (!selectedOrgId) {
			setCustomers([]);
			setSelectedSegments([]);
			return;
		}

		try {
			const response = await getCustomers({ orgId: selectedOrgId });
			const fetchedCustomers = response.customers || [];
			setCustomers(fetchedCustomers);

			const options = [...new Set(fetchedCustomers.map((item) => item.segment))];
			setSelectedSegments(options);
		} catch (error) {
			showToast(`Failed to load customers: ${error.message}`, "error");
		}
	}, [isOrganizationsLoading, location.pathname, selectedOrgId, showToast]);

	const loadDashboardData = useCallback(async () => {
		if (isOrganizationsLoading) {
			return;
		}

		if (!selectedOrgId) {
			setKpis(EMPTY_KPIS);
			setSegmentSummary(EMPTY_SEGMENT_SUMMARY);
			setRevenueTrend(EMPTY_REVENUE_TREND);
			setTopProducts(EMPTY_TOP_PRODUCTS);
			setIsAnalysisDataReady(true);
			setIsLoading(false);
			return;
		}

		try {
			const isCustomerScoped = Boolean(selectedCustomerId);
			const requests = {
				kpis: getKpis({ orgId: selectedOrgId }),
				trend: getRevenueTrend({ customerId: selectedCustomerId, orgId: selectedOrgId }),
				products: getTopProducts({ customerId: selectedCustomerId, orgId: selectedOrgId }),
			};

			if (!isCustomerScoped) {
				requests.summary = getSegmentSummary({ orgId: selectedOrgId });
			}

			const entries = Object.entries(requests);
			const results = await Promise.allSettled(entries.map(([, request]) => request));
			const data = Object.fromEntries(
				results
					.map((result, index) => [entries[index][0], result])
					.filter(([, result]) => result.status === "fulfilled")
					.map(([key, result]) => [key, result.value])
			);

			if (data.kpis) {
				setKpis(data.kpis);
			}
			if (!isCustomerScoped && data.summary) {
				setSegmentSummary(data.summary);
			}
			if (data.trend) {
				setRevenueTrend(data.trend);
			}
			if (data.products) {
				setTopProducts(data.products);
			}
		} catch (error) {
			showToast(`Failed to load dashboard data: ${error.message}`, "error");
		} finally {
			setIsAnalysisDataReady(true);
			setIsLoading(false);
		}
	}, [isOrganizationsLoading, selectedCustomerId, selectedOrgId, showToast]);

	const loadSegmentMessageTemplates = useCallback(async (orgId = selectedOrgId) => {
		const targetOrgId = normalizeOrgId(orgId);
		if (!targetOrgId) {
			setSegmentMessageTemplates({});
			return;
		}

		try {
			const data = await getSegmentMessageTemplates({ orgId: targetOrgId });
			const templateMap = {};
			(data?.templates || []).forEach((item) => {
				templateMap[`${item.segment}::${item.action_type}`] = String(item.message_body || "")
					.replace(/\\r\\n/g, "\n")
					.replace(/\\n/g, "\n")
					.replace(/\\r/g, "\n");
			});
			setSegmentMessageTemplates(templateMap);
		} catch {
			setSegmentMessageTemplates({});
		}
	}, [selectedOrgId]);

	const loadSuggestiveActionsConfig = useCallback(async (orgId = selectedOrgId) => {
		const targetOrgId = normalizeOrgId(orgId);
		if (!targetOrgId) {
			setSuggestiveActionsConfig({
				custom_actions: [],
				hidden_action_keys: [],
				deleted_action_keys: [],
				usage_count: {},
			});
			return;
		}

		try {
			const data = await getSuggestiveActionsConfig({ orgId: targetOrgId });
			setSuggestiveActionsConfig({
				custom_actions: data?.custom_actions || [],
				hidden_action_keys: data?.hidden_action_keys || [],
				deleted_action_keys: data?.deleted_action_keys || [],
				usage_count: data?.usage_count || {},
			});
		} catch {
			setSuggestiveActionsConfig({
				custom_actions: [],
				hidden_action_keys: [],
				deleted_action_keys: [],
				usage_count: {},
			});
		}
	}, [selectedOrgId]);

	const loadActionsContext = useCallback(async () => {
		if (!selectedOrgId) {
			setOrganizationName("Your Shop");
			return;
		}

		try {
			const data = await getActionsContext({ orgId: selectedOrgId });
			setOrganizationName(data?.organization_name || "Your Shop");
		} catch {
			setOrganizationName("Your Shop");
		}
	}, [selectedOrgId]);

	const loadCustomerTimeline = useCallback(async () => {
		if (!selectedOrgId || !selectedCustomerId) {
			setTimeline(EMPTY_TIMELINE);
			return;
		}

		setIsTimelineLoading(true);
		try {
			const data = await getCustomerTimeline(selectedCustomerId, { orgId: selectedOrgId });
			setTimeline(data || EMPTY_TIMELINE);
		} catch (error) {
			setTimeline(EMPTY_TIMELINE);
			showToast(`Failed to load customer timeline: ${error.message}`, "error");
		} finally {
			setIsTimelineLoading(false);
		}
	}, [selectedCustomerId, selectedOrgId, showToast]);

	const hydrateAnalysisData = useCallback(async (orgId) => {
		if (!orgId) {
			setIsAnalysisDataReady(true);
			return;
		}

		setIsAnalysisDataReady(false);
		setIsLoading(true);
		try {
			const entries = [
				["customers", getCustomers({ orgId })],
				["kpis", getKpis({ orgId })],
				["summary", getSegmentSummary({ orgId })],
				["trend", getRevenueTrend({ orgId })],
				["products", getTopProducts({ orgId })],
				["context", getActionsContext({ orgId })],
			];
			const results = await Promise.allSettled(entries.map(([, request]) => request));
			const data = Object.fromEntries(
				results
					.map((result, index) => [entries[index][0], result])
					.filter(([, result]) => result.status === "fulfilled")
					.map(([key, result]) => [key, result.value])
			);

			if (data.customers) {
				const fetchedCustomers = data.customers?.customers || [];
				setCustomers(fetchedCustomers);

				const options = [...new Set(fetchedCustomers.map((item) => item.segment))];
				setSelectedSegments(options);
			}

			if (data.kpis) {
				setKpis(data.kpis);
			}
			if (data.summary) {
				setSegmentSummary(data.summary);
			}
			if (data.trend) {
				setRevenueTrend(data.trend);
			}
			if (data.products) {
				setTopProducts(data.products);
			}
			if (data.context) {
				setOrganizationName(data.context?.organization_name || "Your Shop");
			}
		} catch (error) {
			showToast(`Failed to load dashboard data: ${error.message}`, "error");
		} finally {
			setIsAnalysisDataReady(true);
			setIsLoading(false);
		}
	}, [showToast]);

	useEffect(() => {
		if (isEmbeddedMode) {
			const embeddedOrgId = getEmbeddedOrgId();
			if (String(getAuthToken() || "").trim() && embeddedOrgId) {
				setAuthProfile({
					email: "",
					full_name: "Embedded Viewer",
					role: "user",
					auth_provider: "embedded",
					password_change_required: false,
					can_create_org_without_approval: false,
					can_manage_employees: false,
					show_gemini_signature: false,
					organizations: [{ org_id: embeddedOrgId, name: "Embedded Organization", access_status: "approved" }],
				});
				setOrganizations([{ org_id: embeddedOrgId, name: "Embedded Organization", access_status: "approved" }]);
				setSelectedOrgId(embeddedOrgId);
				setIsAuthenticated(true);
				setIsTrialStatusLoaded(true);
				setIsOrganizationsLoading(false);
				setIsLoading(false);
				setIsAuthBootstrapping(false);
			} else {
				setIsAuthenticated(false);
				setIsLoading(false);
				setIsAuthBootstrapping(false);
				setIsOrganizationsLoading(false);
			}
			return undefined;
		}

		let mounted = true;
		setIsAuthMethodsLoading(true);
		(async () => {
			try {
				const methods = await getAuthMethods();
				if (!mounted) {
					return;
				}
				setAuthMethods({
					password_enabled: Boolean(methods?.password_enabled),
					oauth_enabled: Boolean(methods?.oauth_enabled),
					show_use_another_google_account: methods?.show_use_another_google_account !== false,
				});
			} catch {
				if (!mounted) {
					return;
				}
				setAuthMethods({
					password_enabled: true,
					oauth_enabled: true,
					show_use_another_google_account: true,
				});
			} finally {
				if (mounted) {
					setIsAuthMethodsLoading(false);
				}
			}
		})();

		return () => {
			mounted = false;
		};
	}, [isAuthenticated, isEmbeddedMode]);

	useEffect(() => {
		if (!isAuthenticated || !authProfile?.email) {
			return;
		}
		if (typeof window !== "undefined") {
			window.localStorage.setItem(LAST_LOGOUT_EMAIL_KEY, normalizeAccountEmail(authProfile.email));
		}
		upsertSavedAccount({
			email: authProfile.email,
			role: authProfile.role,
			auth_provider: authProfile.auth_provider,
		});
	}, [authProfile, isAuthenticated]);

	useEffect(() => {
		if (!isAuthenticated || !currentAccountEmail) {
			return;
		}
		if (!selectedOrgId) {
			return;
		}
		writeRememberedOrgId(currentAccountEmail, selectedOrgId);
	}, [currentAccountEmail, isAuthenticated, selectedOrgId]);

	useEffect(() => {
		if (!isAuthenticated || !authProfile?.email) {
			return;
		}

		const syncTokensToSavedAccounts = () => {
			upsertSavedAccount({
				email: authProfile.email,
				role: authProfile.role,
				auth_provider: authProfile.auth_provider,
			});
		};

		syncTokensToSavedAccounts();
		const intervalId = window.setInterval(syncTokensToSavedAccounts, 60000);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [authProfile, isAuthenticated]);

	useEffect(() => {
		if (isAuthenticated || LOGIN_HERO_SLIDES.length <= 1) {
			return;
		}
		const intervalId = window.setInterval(() => {
			setActiveHeroSlide((current) => (current + 1) % LOGIN_HERO_SLIDES.length);
		}, 5200);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [isAuthenticated]);

	useEffect(() => {
		const unsubscribe = subscribeToAuthExpired((detail) => {
			const message =
				String(detail?.message || "").trim() ||
				"Session expired. Please login again.";
			handleSessionExpired(message);
		});

		return unsubscribe;
	}, [handleSessionExpired]);

	useEffect(() => {
		if (isEmbeddedMode) {
			setIsAuthBootstrapping(false);
			return undefined;
		}

		let mounted = true;
		(async () => {
			try {
				setIsAuthBootstrapping(true);
				const refreshToken = String(getAuthRefreshToken() || "").trim();
				let accessToken = String(getAuthToken() || "").trim();

				if (!accessToken && !refreshToken) {
					if (!mounted) {
						return;
					}
					setIsAuthenticated(false);
					setAuthProfile(null);
					setIsLoading(false);
					setIsAuthBootstrapping(false);
					return;
				}

				if (!accessToken && refreshToken) {
					await refreshAuthSession();
					accessToken = String(getAuthToken() || "").trim();
				}

				let profile = null;
				try {
					profile = await getCurrentUserProfile();
				} catch (error) {
					const status = Number(error?.response?.status || 0);
					if (status === 401 && refreshToken) {
						await refreshAuthSession();
						profile = await getCurrentUserProfile();
					} else {
						throw error;
					}
				}

				if (!mounted) {
					return;
				}
				setAuthProfile(profile || null);
				setPasswordChangeRequired(Boolean(profile?.password_change_required));
				sessionExpiredHandledRef.current = false;
				setIsAuthenticated(true);
				setIsLoading(false);
				setIsAuthBootstrapping(false);
			} catch (error) {
				if (!mounted) {
					return;
				}
				const status = Number(error?.response?.status || 0);
				const isExploreSession = Boolean(getExploreTrialId());
				if (isExploreSession && String(getAuthToken() || "").trim() && status >= 500) {
					setAuthProfile({
						email: "",
						role: "user",
						auth_provider: "oauth",
						is_explore_user: true,
						org_ids: [DEMO_ORG_ID],
					});
					setPasswordChangeRequired(false);
					setIsAuthenticated(true);
					setIsLoading(false);
					setIsAuthBootstrapping(false);
					return;
				}
				setIsAuthBootstrapping(false);
				handleSessionExpired(
					error?.response?.data?.detail ||
					error?.response?.data?.message ||
					"Session is invalid. Please login again."
				);
			}
		})();

		return () => {
			mounted = false;
		};
	}, [
		isAuthenticated,
		handleSessionExpired,
		isEmbeddedMode,
	]);

	useEffect(() => {
		if (!isAuthenticated || passwordChangeRequired) {
			return;
		}

		if (isEmbeddedMode) {
			loadSegmentMessageTemplates();
			loadSuggestiveActionsConfig();
			return;
		}

		loadOrganizations();
		if (getExploreTrialId()) {
			const timer = window.setTimeout(() => {
				loadSegmentMessageTemplates();
				loadSuggestiveActionsConfig();
			}, 1200);
			return () => window.clearTimeout(timer);
		}

		loadSegmentMessageTemplates();
		loadSuggestiveActionsConfig();
	}, [
		isAuthenticated,
		isEmbeddedMode,
		passwordChangeRequired,
		loadOrganizations,
		loadSegmentMessageTemplates,
		loadSuggestiveActionsConfig,
	]);

	useEffect(() => {
		if (isEmbeddedMode) {
			if (location.pathname !== "/analysis") {
				navigate("/analysis?embedded=1", { replace: true });
			}
			return;
		}

		if (!isAuthenticated || passwordChangeRequired || isOrganizationsLoading) {
			return;
		}

		if (location.pathname === "/explore") {
			return;
		}

		if (isTrialRestricted && location.pathname !== "/analysis") {
			navigate("/analysis", { replace: true });
			return;
		}

		if (isTrialStatusLoaded && !isTrialAccount && hasSingleOrganization && !isClientManager && location.pathname !== "/analysis") {
			const singleOrgId = String(organizations[0]?.org_id || "");
			if (singleOrgId && selectedOrgId !== singleOrgId) {
				setSelectedOrgId(singleOrgId);
			}
			navigate("/analysis", { replace: true });
		}
	}, [
		isClientManager,
		isEmbeddedMode,
		hasSingleOrganization,
		isAuthenticated,
		isOrganizationsLoading,
		isTrialAccount,
		isTrialRestricted,
		isTrialStatusLoaded,
		location.pathname,
		navigate,
		organizations,
		passwordChangeRequired,
		selectedOrgId,
	]);

	useEffect(() => {
		if (location.pathname === "/analysis") {
			setSelectedDashboardMetric("clients");
		}
	}, [location.pathname, selectedOrgId]);

	useEffect(() => {
		if (!isAuthenticated || passwordChangeRequired || isOrganizationsLoading || !selectedOrgId) {
			return;
		}
		if (location.pathname === "/analysis") {
			return;
		}
		loadCustomers();
		loadActionsContext();
	}, [isAuthenticated, passwordChangeRequired, isOrganizationsLoading, location.pathname, selectedOrgId, loadCustomers, loadActionsContext]);

	useEffect(() => {
		setSegmentActionContext(null);
		setSegmentMessageTemplates({});
		setSuggestiveActionsConfig({
			custom_actions: [],
			hidden_action_keys: [],
			deleted_action_keys: [],
			usage_count: {},
		});
		setSelectedCustomerId(null);
		setTimeline(EMPTY_TIMELINE);
		if (isAuthenticated) {
			setIsAnalysisDataReady(false);
			setIsLoading(true);
		}
	}, [selectedOrgId, isAuthenticated]);

	useEffect(() => {
		if (isEmbeddedMode) {
			return;
		}
		if (!isAuthenticated || passwordChangeRequired) {
			setIsTrialAccount(false);
			setIsTrialStatusLoaded(false);
			return;
		}
		setIsTrialAccount(Boolean(getExploreTrialId()));
		setIsTrialStatusLoaded(true);
	}, [isAuthenticated, isEmbeddedMode, passwordChangeRequired]);

	useEffect(() => {
		if (!selectedCustomerId) {
			return;
		}
		const existsInCurrentRows = customers.some(
			(item) => item.customer_id === selectedCustomerId
		);
		if (!existsInCurrentRows) {
			setSelectedCustomerId(null);
		}
	}, [customers, selectedCustomerId]);

	useEffect(() => {
		if (!isAuthenticated || passwordChangeRequired || isOrganizationsLoading) {
			return;
		}
		loadDashboardData();
	}, [isAuthenticated, passwordChangeRequired, isOrganizationsLoading, loadDashboardData]);

	useEffect(() => {
		if (!isAuthenticated || passwordChangeRequired) {
			return;
		}
		loadCustomerTimeline();
	}, [isAuthenticated, passwordChangeRequired, loadCustomerTimeline]);

	useEffect(() => {
		if (!isAuthenticated || passwordChangeRequired || isOrganizationsLoading || location.pathname !== "/analysis" || !selectedOrgId) {
			return;
		}

		hydrateAnalysisData(selectedOrgId);
	}, [hydrateAnalysisData, isAuthenticated, passwordChangeRequired, isOrganizationsLoading, location.pathname, selectedOrgId]);

	useEffect(() => {
		if (isEmbeddedMode) {
			return;
		}
		if (isLoggingOut) {
			return;
		}

		if (!isAuthenticated) {
			setIsOrganizationsLoading(true);
			return;
		}

		const pathname = location.pathname;
		const search = location.search || "";
		const params = new URLSearchParams(search);
		const hasOAuthPayload = Boolean((params.get("code") || "").trim() || (params.get("error") || "").trim());
		const isResumingAddAccount = params.get("add_account") === "1";
		if (passwordChangeRequired && pathname !== "/change-password") {
			navigate("/change-password", { replace: true });
			return;
		}

		if (!passwordChangeRequired && pathname === "/change-password") {
			navigate("/onboarding", { replace: true });
			return;
		}

		if (!isTrialStatusLoaded && (pathname === "/" || pathname === "/callback" || pathname === "/onboarding")) {
			return;
		}

		if ((pathname === "/" || pathname === "/callback") && hasOAuthPayload) {
			return;
		}

		if ((pathname === "/" || pathname === "/callback") && isResumingAddAccount) {
			return;
		}

		if (pathname === "/" || pathname === "/callback") {
			const destination = passwordChangeRequired ? "/change-password" : (isTrialRestricted ? "/analysis" : "/onboarding");
			navigate(destination, { replace: true });
			return;
		}

		if (isTrialRestricted && pathname !== "/analysis") {
			navigate("/analysis", { replace: true });
			return;
		}

		if (pathname === "/admin" && !isAuthBootstrapping && !isAdmin) {
			navigate("/analysis", { replace: true });
			return;
		}

		if (pathname === "/team" && !isAuthBootstrapping && !canManageEmployees) {
			navigate("/analysis", { replace: true });
			return;
		}

		if (
			pathname !== "/onboarding" &&
			pathname !== "/analysis" &&
			pathname !== "/admin" &&
			pathname !== "/change-password" &&
			pathname !== "/team" &&
			pathname !== "/explore"
		) {
			navigate(passwordChangeRequired ? "/change-password" : (isTrialRestricted ? "/analysis" : "/onboarding"), { replace: true });
		}
	}, [canManageEmployees, isAdmin, isAuthenticated, isAuthBootstrapping, isEmbeddedMode, isLoggingOut, isTrialRestricted, isTrialStatusLoaded, location.pathname, location.search, navigate, passwordChangeRequired]);

	useEffect(() => {
		if (isEmbeddedMode) {
			if (location.pathname !== "/analysis") {
				navigate("/analysis?embedded=1", { replace: true });
			}
			return;
		}
		if (isAuthenticated) {
			return;
		}

		const pathname = location.pathname;
		if (pathname !== "/" && pathname !== "/callback" && pathname !== "/explore") {
			navigate("/", { replace: true });
		}
	}, [isAuthenticated, isEmbeddedMode, location.pathname, navigate]);

	useEffect(() => {
		if (!toast.message) {
			return undefined;
		}

		const timer = setTimeout(() => {
			setToast({ message: "", type: "info" });
		}, 3200);

		return () => clearTimeout(timer);
	}, [toast]);

	useEffect(() => {
		if (typeof window === "undefined" || typeof document === "undefined") {
			return undefined;
		}

		let resizeTimer;
		const onResize = () => {
			document.body.classList.add("is-resizing");
			window.clearTimeout(resizeTimer);
			resizeTimer = window.setTimeout(() => {
				document.body.classList.remove("is-resizing");
			}, 140);
		};

		window.addEventListener("resize", onResize, { passive: true });
		return () => {
			window.removeEventListener("resize", onResize);
			window.clearTimeout(resizeTimer);
			document.body.classList.remove("is-resizing");
		};
	}, []);

	function onSort(columnKey) {
		if (sortKey === columnKey) {
			setSortDirection((direction) => direction * -1);
		} else {
			setSortKey(columnKey);
			setSortDirection(-1);
		}
	}

	function onTakeSegmentAction(segmentItem) {
		setSegmentActionContext({
			segment: segmentItem.segment,
			customerCount: segmentItem.customer_count || 0,
			revenue: segmentItem.revenue || 0,
		});
	}

	const loadWhatsAppCampaignWorkspace = useCallback(async (orgId = selectedOrgId) => {
		if (!orgId) {
			setWhatsappTemplates([]);
			setWhatsappTemplateLoadError("");
			setWhatsappAnalytics(null);
			return;
		}

		try {
			const [templatesResponse, analyticsResponse] = await Promise.all([
				getWhatsAppTemplates({ orgId }),
				getWhatsAppAnalytics({ orgId }),
			]);
			setWhatsappTemplates(templatesResponse?.templates || []);
			setWhatsappTemplateLoadError("");
			setWhatsappAnalytics(analyticsResponse || null);
		} catch (error) {
			setWhatsappTemplates([]);
			setWhatsappTemplateLoadError(error?.response?.data?.detail || error?.message || "Failed to load WhatsApp templates.");
			setWhatsappAnalytics(null);
		}
	}, [selectedOrgId]);

	useEffect(() => {
		if (!isAuthenticated || !selectedOrgId || !canUseWhatsApp) {
			setWhatsappConnection(null);
			setWhatsappTemplates([]);
			setWhatsappTemplateLoadError("");
			setWhatsappAnalytics(null);
			return;
		}

		let cancelled = false;
		setWhatsappConnection({ connected: false, org_id: selectedOrgId });
		setWhatsappTemplates([]);
		setWhatsappTemplateLoadError("");
		setWhatsappAnalytics(null);
		getWhatsAppConnection({ orgId: selectedOrgId })
			.then((connection) => {
				if (!cancelled) {
					setWhatsappConnection(connection);
					if (connection?.connected) {
						loadWhatsAppCampaignWorkspace(selectedOrgId);
					} else {
						setWhatsappTemplates([]);
						setWhatsappTemplateLoadError("");
						setWhatsappAnalytics(null);
					}
				}
			})
			.catch(() => {
				if (!cancelled) {
					setWhatsappConnection({ connected: false, org_id: selectedOrgId });
					setWhatsappTemplates([]);
					setWhatsappAnalytics(null);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [canUseWhatsApp, isAuthenticated, loadWhatsAppCampaignWorkspace, selectedOrgId]);

	async function onConnectWhatsApp() {
		if (!canUseWhatsApp) {
			return;
		}
		if (!selectedOrgId) {
			showToast("Select a business workspace before connecting WhatsApp.", "error");
			return;
		}
		if (whatsappConnection?.connected) {
			try {
				const connection = await getWhatsAppConnection({ orgId: selectedOrgId });
				setWhatsappConnection(connection);
				if (connection?.connected) {
					const summary = formatWhatsAppConnectionSummary(connection);
					showToast(
						summary ? `WhatsApp already connected. ${summary}` : "WhatsApp is already connected for this business.",
						"info"
					);
					return;
				}
			} catch (_error) {
				showToast("WhatsApp is already connected for this business.", "info");
				return;
			}
		}

		setIsWhatsAppConnecting(true);
		let sessionInfo = {};
		const onSignupMessage = (event) => {
			const origin = String(event.origin || "");
			if (!origin.endsWith("facebook.com") && !origin.endsWith("facebook.net")) {
				return;
			}
			let payload = event.data;
			if (typeof payload === "string") {
				try {
					payload = JSON.parse(payload);
				} catch (_err) {
					return;
				}
			}
			if (payload?.type === "WA_EMBEDDED_SIGNUP" || payload?.event === "FINISH" || payload?.data) {
				sessionInfo = payload;
			}
		};

		try {
			const config = await getWhatsAppEmbeddedSignupConfig();
			const FB = await loadFacebookSdk();
			FB.init({
				appId: config.app_id,
				autoLogAppEvents: true,
				xfbml: false,
				version: config.graph_api_version,
			});

			window.addEventListener("message", onSignupMessage);
			const loginResponse = await new Promise((resolve, reject) => {
				FB.login(
					(response) => {
						if (!response?.authResponse?.code) {
							reject(new Error("Embedded Signup did not return an authorization code."));
							return;
						}
						resolve(response);
					},
					{
						config_id: config.config_id,
						response_type: "code",
						override_default_response_type: true,
						extras: {
							sessionInfoVersion: String(config.session_info_version || 3),
							featureType: "whatsapp_business_app_onboarding",
						},
					}
				);
			});

			const signupIds = extractWhatsAppSignupIds(sessionInfo);
			const saved = await completeWhatsAppEmbeddedSignup(
				{
					code: loginResponse.authResponse.code,
					waba_id: signupIds.waba_id,
					phone_number_id: signupIds.phone_number_id,
					display_phone_number: signupIds.display_phone_number,
					business_id: signupIds.business_id,
					signup_response: sessionInfo || {},
				},
				{ orgId: selectedOrgId }
			);
			setWhatsappConnection(saved);
			if (saved?.connected) {
				await loadWhatsAppCampaignWorkspace(selectedOrgId);
			}
			showToast("WhatsApp connected for this business.", "success");
		} catch (error) {
			showToast(error?.response?.data?.detail || error?.message || "Failed to connect WhatsApp.", "error");
		} finally {
			window.removeEventListener("message", onSignupMessage);
			setIsWhatsAppConnecting(false);
		}
	}

	async function onDisconnectWhatsApp() {
		if (!canUseWhatsApp) {
			return;
		}
		if (!selectedOrgId) {
			showToast("Select a business workspace before disconnecting WhatsApp.", "error");
			return;
		}

		setIsWhatsAppDisconnecting(true);
		try {
			const disconnected = await disconnectWhatsAppConnection({ orgId: selectedOrgId });
			setWhatsappConnection(disconnected || { connected: false, org_id: selectedOrgId });
			setWhatsappTemplates([]);
			setWhatsappTemplateLoadError("");
			setWhatsappAnalytics(null);
			showToast("WhatsApp disconnected for this business.", "success");
		} catch (error) {
			showToast(error?.response?.data?.detail || error?.message || "Failed to disconnect WhatsApp.", "error");
		} finally {
			setIsWhatsAppDisconnecting(false);
		}
	}

	async function onSendWhatsAppCampaign(payload) {
		if (!selectedOrgId) {
			showToast("Select a business workspace before sending WhatsApp campaign.", "error");
			return null;
		}
		const isTestSend =
			payload?.action_type === "whatsapp_template_test" ||
			(Array.isArray(payload?.test_phone_numbers) && payload.test_phone_numbers.length > 0);
		try {
			const result = await sendWhatsAppSegmentCampaign(payload, { orgId: selectedOrgId });
			const sent = Number(result?.sent || 0);
			const failed = Number(result?.failed || 0);
			const recipients = Number(result?.recipients || 0);
			const firstFailure = Array.isArray(result?.failures) ? result.failures.find((item) => item?.reason) : null;
			showToast(
				isTestSend
					? failed
						? `WhatsApp test sent ${sent}/${recipients}; ${failed} failed${firstFailure?.reason ? `: ${firstFailure.reason}` : "."}`
						: `WhatsApp test sent to ${sent} number${sent === 1 ? "" : "s"}.`
					: failed
						? `WhatsApp campaign accepted ${sent}/${recipients}; ${failed} failed.`
						: `WhatsApp campaign sent to ${sent} ${payload?.segment || "segment"} customers.`,
				failed ? "info" : "success"
			);
			await loadWhatsAppCampaignWorkspace(selectedOrgId);
			if (!isTestSend && selectedCustomer && selectedCustomer.segment === payload?.segment) {
				await loadCustomerTimeline();
			}
			if (!isTestSend) {
				setSegmentActionContext(null);
			}
			return result;
		} catch (error) {
			showToast(error?.response?.data?.detail || error?.message || (isTestSend ? "Failed to send WhatsApp test." : "Failed to send WhatsApp campaign."), "error");
			throw error;
		}
	}

	async function onCreateWhatsAppTemplateRequest(payload) {
		if (!selectedOrgId) {
			showToast("Select a business workspace before requesting a WhatsApp template.", "error");
			return null;
		}
		try {
			const result = await createWhatsAppTemplateRequest(payload, { orgId: selectedOrgId });
			showToast("WhatsApp template submitted to Meta for review.", "success");
			await loadWhatsAppCampaignWorkspace(selectedOrgId);
			return result;
		} catch (error) {
			showToast(error?.response?.data?.detail || error?.message || "Failed to submit WhatsApp template.", "error");
			throw error;
		}
	}

	async function onRunSegmentAction(action, messageBody, recipientCustomerIds = null) {
		if (!segmentActionContext) {
			return;
		}

		try {
			const normalizedMessage = (messageBody || "").trim();

			if (Array.isArray(recipientCustomerIds) && recipientCustomerIds.length) {
				const selectedIdSet = new Set(recipientCustomerIds);
				const targetCustomers = customers.filter(
					(item) =>
						selectedIdSet.has(item.customer_id) &&
						item.segment === segmentActionContext.segment
				);

				if (!targetCustomers.length) {
					showToast("No valid recipients selected for this segment.", "error");
					return;
				}

				await Promise.all(
					targetCustomers.map((customerItem) =>
						logCustomerAction({
							customer_id: customerItem.customer_id,
							segment: segmentActionContext.segment,
							source: "segment_overview_targeted",
							action_type: action.type,
							title: action.title,
							note: action.note,
							message_body: normalizedMessage || null,
						}, { orgId: selectedOrgId })
					)
				);

				showToast(
					`Logged for ${targetCustomers.length} selected ${segmentActionContext.segment} customers.`,
					"success"
				);
				if (selectedCustomer && selectedIdSet.has(selectedCustomer.customer_id)) {
					await loadCustomerTimeline();
				}
				setSegmentActionContext(null);
				return;
			}

			const result = await logSegmentAction({
				segment: segmentActionContext.segment,
				action_type: action.type,
				title: action.title,
				note: action.note,
				custom_message: normalizedMessage || null,
				recipient_customer_ids:
					Array.isArray(recipientCustomerIds) && recipientCustomerIds.length
						? recipientCustomerIds
						: null,
			}, { orgId: selectedOrgId });
			showToast(
				`Logged for ${result.recipients} ${segmentActionContext.segment} customers.`,
				"success"
			);
			if (selectedCustomer && selectedCustomer.segment === segmentActionContext.segment) {
				await loadCustomerTimeline();
			}
			setSegmentActionContext(null);
		} catch (error) {
			showToast(`Failed to log segment action: ${error.message}`, "error");
		}
	}

	async function onResetSegmentTemplate(segment, actionType) {
		try {
			await resetSegmentMessageTemplate({
				segment,
				action_type: actionType,
			});
			setSegmentMessageTemplates((current) => {
				const next = { ...current };
				delete next[`${segment}::${actionType}`];
				return next;
			});
			showToast("Template reset to default.", "success");
		} catch (error) {
			showToast(`Failed to reset template: ${error.message}`, "error");
		}
	}

	async function onCreateSuggestiveAction(segment, payload) {
		try {
			const created = await createCustomSuggestiveAction({
				segment,
				title: payload.title,
				note: payload.note,
				message_template: payload.messageTemplate,
			});
			setSuggestiveActionsConfig((current) => ({
				...current,
				custom_actions: [...(current.custom_actions || []), created],
			}));
			showToast("New suggestive action created.", "success");
		} catch (error) {
			showToast(`Failed to create action: ${error.message}`, "error");
		}
	}

	async function onUpdateSuggestiveAction(segment, actionType, payload) {
		try {
			const normalizedMessage = (payload.messageTemplate || "").trim();
			const updated = await updateCustomSuggestiveAction({
				segment,
				action_type: actionType,
				title: payload.title,
				note: payload.note,
				message_template: normalizedMessage || null,
			});

			setSuggestiveActionsConfig((current) => ({
				...current,
				custom_actions: (current.custom_actions || []).map((item) =>
					item.segment === segment && item.action_type === actionType ? updated : item
				),
			}));

			setSegmentMessageTemplates((current) => {
				const key = `${segment}::${actionType}`;
				const next = { ...current };
				if (normalizedMessage) {
					next[key] = normalizedMessage;
				} else {
					delete next[key];
				}
				return next;
			});

			showToast("Suggestive action updated.", "success");
		} catch (error) {
			showToast(`Failed to update action: ${error.message}`, "error");
		}
	}

	async function onToggleSuggestiveAction(segment, actionType, hidden) {
		try {
			await toggleSuggestiveActionVisibility({
				segment,
				action_type: actionType,
				hidden,
			});
			setSuggestiveActionsConfig((current) => {
				const set = new Set(current.hidden_action_keys || []);
				const key = `${segment}::${actionType}`;
				if (hidden) set.add(key);
				else set.delete(key);
				return {
					...current,
					hidden_action_keys: Array.from(set).sort(),
				};
			});
		} catch (error) {
			showToast(`Failed to update visibility: ${error.message}`, "error");
		}
	}

	async function onDeleteSuggestiveAction(segment, actionType) {
		try {
			await deleteCustomSuggestiveAction({ segment, action_type: actionType });
			setSuggestiveActionsConfig((current) => ({
				...current,
				custom_actions: (current.custom_actions || []).filter(
					(item) => !(item.segment === segment && item.action_type === actionType)
				),
				hidden_action_keys: (current.hidden_action_keys || []).filter(
					(item) => item !== `${segment}::${actionType}`
				),
				deleted_action_keys: Array.from(
					new Set([...(current.deleted_action_keys || []), `${segment}::${actionType}`])
				).sort(),
			}));
			showToast("Custom suggestive action deleted.", "success");
		} catch (error) {
			showToast(`Failed to delete action: ${error.message}`, "error");
		}
	}

	async function onBulkToggleSuggestiveActions(segment, actionTypes, hidden) {
		try {
			await toggleSuggestiveActionsBulkVisibility({
				segment,
				action_types: actionTypes,
				hidden,
			});
			setSuggestiveActionsConfig((current) => {
				const set = new Set(current.hidden_action_keys || []);
				actionTypes.forEach((actionType) => {
					const key = `${segment}::${actionType}`;
					if (hidden) set.add(key);
					else set.delete(key);
				});
				return {
					...current,
					hidden_action_keys: Array.from(set).sort(),
				};
			});
		} catch (error) {
			showToast(`Failed to update actions: ${error.message}`, "error");
		}
	}

	async function onResetSuggestiveActionsSegment(segment) {
		try {
			await resetSegmentSuggestiveActions({ segment });
			await Promise.allSettled([
				loadSegmentMessageTemplates(),
				loadSuggestiveActionsConfig(),
			]);
			showToast("Segment actions reset to defaults.", "success");
		} catch (error) {
			showToast(`Failed to reset segment actions: ${error.message}`, "error");
		}
	}

	async function onPipelineComplete(importedOrgId) {
		const targetOrgId = normalizeOrgId(importedOrgId || selectedOrgId);

		if (targetOrgId) {
			setSelectedOrgId(targetOrgId);
		}

		await Promise.allSettled([
			loadOrganizations(),
			loadSegmentMessageTemplates(targetOrgId),
			loadSuggestiveActionsConfig(targetOrgId),
		]);

		if (targetOrgId) {
			await Promise.allSettled([
				recalculateRfm({ orgId: targetOrgId }),
			]);

			setNewlyCreatedOrgIds((current) =>
				current.filter((orgId) => orgId !== targetOrgId)
			);
			closeOnboardingPopup();
			navigate("/analysis");
		}
	}

	function onImportOrgCreated(createdOrg) {
		const normalizedOrg = normalizeOrganization(createdOrg);
		if (!normalizedOrg?.org_id) {
			return;
		}

		setOrganizations((current) => {
			if (current.some((item) => item.org_id === normalizedOrg.org_id)) {
				return current;
			}
			return [normalizedOrg, ...current];
		});
		setSelectedOrgId(normalizedOrg.org_id);
		setPendingNewOrgName("");
		setNewlyCreatedOrgIds((current) =>
			current.includes(normalizedOrg.org_id)
				? current
				: [...current, normalizedOrg.org_id]
		);
	}

	function onOrgSelectionChange(orgId) {
		const normalizedOrgId = normalizeOrgId(orgId);
		setSelectedOrgId(normalizedOrgId);
		writeRememberedOrgId(currentAccountEmail, normalizedOrgId);
		return normalizedOrgId;
	}

	function onOnboardingOrgChange(orgId) {
		const normalizedOrgId = onOrgSelectionChange(orgId);
		clearGeneratedOrgReport();
		if (normalizedOrgId) {
			setPendingNewOrgName("");
		}
	}

	function openOnboardingPopup(type) {
		setOnboardingPopup(type);
	}

	function closeOnboardingPopup() {
		setOnboardingPopup(null);
		setRequestOrganizationName("");
		clearGeneratedOrgReport();
	}

	function clearGeneratedOrgReport() {
		if (orgReportUrlRef.current) {
			URL.revokeObjectURL(orgReportUrlRef.current);
		}
		orgReportUrlRef.current = "";
		setOrgReportUrl("");
		setOrgReportHtml("");
	}

	function openReportContactPrompt() {
		if (!selectedOrgId || isGeneratingOrgReport) {
			return;
		}
		setReportContactForm({
			contact_person_name: selectedOrg?.report_contact_person_name || "",
			contact_phone: selectedOrg?.report_contact_phone || "",
			contact_address: selectedOrg?.report_contact_address || "",
		});
		setIsReportContactOpen(true);
	}

	async function getReportErrorDetail(error) {
		const data = error?.response?.data;
		if (data instanceof Blob) {
			const text = await data.text();
			if (text) {
				try {
					const parsed = JSON.parse(text);
					return parsed?.detail || parsed?.error || text;
				} catch {
					return text;
				}
			}
		}
		return data?.detail || data?.error || error?.message || "Report generation failed";
	}

	async function onGenerateOrgReport(contactDetails) {
		if (!selectedOrgId || isGeneratingOrgReport) {
			return;
		}

		try {
			setIsGeneratingOrgReport(true);
			clearGeneratedOrgReport();
			showToast(`Preparing ${REPORT_LANGUAGE_LABEL} customer portfolio report...`, "info");
			const blob = await generateCustomerPortfolioReport({
				orgId: selectedOrgId,
				language: REPORT_LANGUAGE,
				contactDetails,
			});
			const html = blob instanceof Blob ? await blob.text() : String(blob || "");
			if (!html.trim()) {
				throw new Error("Report HTML is empty.");
			}
			const reportBlob = new Blob([html], { type: "text/html;charset=utf-8" });
			const reportUrl = URL.createObjectURL(reportBlob);
			orgReportUrlRef.current = reportUrl;
			setOrgReportUrl(reportUrl);
			setOrgReportHtml(html);
			showToast(`${REPORT_LANGUAGE_LABEL} customer portfolio report generated.`, "success");
		} catch (error) {
			const detail = await getReportErrorDetail(error);
			showToast(`Failed to generate report: ${detail}`, "error");
		} finally {
			setIsGeneratingOrgReport(false);
		}
	}

	async function onConfirmReportContact(event) {
		event.preventDefault();
		if (!selectedOrgId || isGeneratingOrgReport) {
			return;
		}
		const contactDetails = {
			contact_person_name: reportContactForm.contact_person_name.trim(),
			contact_phone: reportContactForm.contact_phone.trim(),
			contact_address: reportContactForm.contact_address.trim(),
		};
		if (!contactDetails.contact_person_name) {
			showToast("Enter contact person name before generating the report.", "error");
			return;
		}

		try {
			const updatedOrg = await updateOrganizationReportContact(selectedOrgId, contactDetails);
			setOrganizations((current) =>
				current.map((org) => (org.org_id === updatedOrg.org_id ? { ...org, ...updatedOrg } : org))
			);
			setIsReportContactOpen(false);
			await onGenerateOrgReport(contactDetails);
		} catch (error) {
			showToast(`Failed to save report contact: ${error.message}`, "error");
		}
	}

	function onPrintOrgReport() {
		const frameWindow = orgReportFrameRef.current?.contentWindow;
		if (!frameWindow) {
			showToast("Report preview is still loading. Please try again.", "info");
			return;
		}
		frameWindow.focus();
		frameWindow.print();
	}

	async function onSubmitOrgRequest() {
		const orgName = requestOrganizationName.trim();
		if (!orgName) {
			showToast("Enter organization name to request approval.", "error");
			return;
		}

		try {
			setIsSubmittingOrgRequest(true);
			await createOrganization({ name: orgName });
			showToast("Request sent to Clustrex for approval.", "success");
			closeOnboardingPopup();
		} catch (error) {
			showToast(`Failed to submit organization request: ${error.message}`, "error");
		} finally {
			setIsSubmittingOrgRequest(false);
		}
	}

	async function onGoToAnalysis() {
		if (!canGoToAnalysis) {
			return;
		}
		closeOnboardingPopup();
		navigate("/analysis");
	}

	async function onLoginSubmit(event) {
		event.preventDefault();

		if (isAuthMethodsLoading) {
			setLoginError("Sign-in methods are loading. Please wait a moment.");
			return;
		}

		if (!authMethods.password_enabled) {
			setLoginError("Email/password sign-in is disabled by administrator.");
			return;
		}

		setIsLoginSubmitting(true);
		try {
			const result = await loginWithPassword(loginEmail, loginPassword);
			setAuthProfile(result?.profile || null);
			if (typeof window !== "undefined" && result?.profile?.email) {
				window.localStorage.setItem(LAST_LOGOUT_EMAIL_KEY, normalizeAccountEmail(result.profile.email));
			}
			const mustChangePassword =
				Boolean(result?.passwordChangeRequired) ||
				Boolean(result?.profile?.password_change_required);
			setPasswordChangeRequired(mustChangePassword);
			sessionExpiredHandledRef.current = false;
			setIsAuthenticated(true);
			navigate(mustChangePassword ? "/change-password" : "/onboarding", { replace: true });
			setLoginError("");
			setLoginEmail("");
			setLoginPassword("");
			setIsLoading(true);
		} catch (error) {
			setLoginError(formatAuthError(error, "Login failed. Verify your email/password."));
		} finally {
			setIsLoginSubmitting(false);
		}
	}

	async function onPasswordChangeSubmit(event) {
		event.preventDefault();
		setIsPasswordChangeSubmitting(true);
		setLoginError("");
		try {
			await changeCurrentUserPassword(
				currentPasswordInput,
				newPasswordInput,
				confirmNewPasswordInput,
			);
			setPasswordChangeRequired(false);
			setCurrentPasswordInput("");
			setNewPasswordInput("");
			setConfirmNewPasswordInput("");
			const refreshedProfile = await getCurrentUserProfile();
			setAuthProfile(refreshedProfile || null);
			showToast("Password updated successfully.", "success");
			navigate("/onboarding", { replace: true });
		} catch (error) {
			setLoginError(formatAuthError(error, "Failed to update password."));
		} finally {
			setIsPasswordChangeSubmitting(false);
		}
	}

	async function onLogout() {
		setIsLoggingOut(true);
		const idTokenHint = String(getAuthIdToken() || "").trim();
		const refreshTokenHint = String(getAuthRefreshToken() || "").trim();
		clearSessionState();
		setLoginError("");
		if (typeof window !== "undefined") {
			window.localStorage.removeItem(LAST_LOGOUT_EMAIL_KEY);
			window.sessionStorage.setItem(FORCE_ACCOUNT_CHOOSER_ONCE_KEY, "1");
		}

		navigate("/", { replace: true });

		try {
			if (refreshTokenHint) {
				await silentLogoutSession(refreshTokenHint);
			}
		} catch {
			// Ignore provider-side revoke failures and keep user on local login screen.
		}

		if (!idTokenHint || typeof window === "undefined") {
			return;
		}

		try {
			const redirectUri = `${window.location.origin}`;
			const logoutData = await getOAuthLogoutUrl(redirectUri, {
				idTokenHint,
			});
			if (logoutData?.logout_url) {
				window.location.assign(logoutData.logout_url);
			}
		} catch {
			// If provider logout URL generation fails, keep the user on the local login screen.
		}
	}

	const startOAuthLoginFlow = useCallback(async (options = {}) => {
		if (isOAuthSubmittingRef.current) {
			return;
		}
		try {
			setIsLoggingOut(false);
			isOAuthSubmittingRef.current = true;
			setIsOAuthSubmitting(true);
			setLoginError("");
			setOauthRetryAvailable(false);
			setLoginEmail("");
			setLoginPassword("");

			const redirectUri = `${window.location.origin}`;
			const pkcePair = await generatePkcePair();
			window.sessionStorage.setItem("rfm_oauth_redirect_uri", redirectUri);
			window.sessionStorage.setItem(PKCE_VERIFIER_KEY, pkcePair.verifier);
			const forceAccountSelection = Boolean(options.forceAccountSelection);
			const enforceAccountSwitch = Boolean(options.forceReauthentication);
			const loginHint = String(options.loginHint || "").trim().toLowerCase();
			const data = await getOAuthLoginUrl(redirectUri, {
				forceAccountSelection,
				forceReauthentication: enforceAccountSwitch,
				loginHint,
				codeChallenge: pkcePair.challenge,
				codeChallengeMethod: "S256",
			});
			const state = (data?.state || "").trim();
			if (state) {
				window.sessionStorage.setItem("rfm_oauth_state", state);
			}
			if (!data?.auth_url) {
				throw new Error("OAuth login URL not returned by backend");
			}
			window.location.assign(data.auth_url);
		} catch (error) {
			setLoginError(formatAuthError(error, "Unable to start OAuth login."));
			isOAuthSubmittingRef.current = false;
			setIsOAuthSubmitting(false);
		}
	}, [formatAuthError]);

	const startOAuthLoginAfterProviderLogout = useCallback(async () => {
		if (isOAuthSubmittingRef.current) {
			return;
		}
		try {
			setIsLoggingOut(false);
			isOAuthSubmittingRef.current = true;
			setIsOAuthSubmitting(true);
			setLoginError("");
			setOauthRetryAvailable(false);
			setLoginEmail("");
			setLoginPassword("");

			if (typeof window !== "undefined") {
				window.localStorage.removeItem(LAST_LOGOUT_EMAIL_KEY);
				window.sessionStorage.setItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY, "1");
				window.sessionStorage.removeItem(ADD_ACCOUNT_RETURN_PATH_KEY);
				window.sessionStorage.removeItem(OAUTH_UNAUTHORIZED_ERROR_KEY);
				window.sessionStorage.removeItem(OAUTH_EXPLORE_MISMATCH_ERROR_KEY);
				window.sessionStorage.removeItem("rfm_oauth_state");
				window.sessionStorage.removeItem("rfm_oauth_redirect_uri");
				window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
			}

			const redirectUri = `${window.location.origin}`;
			const logoutData = await getOAuthLogoutUrl(redirectUri, {
				idTokenHint: String(getAuthIdToken() || "").trim(),
			});

			if (!logoutData?.logout_url) {
				throw new Error("OAuth logout URL not returned by backend");
			}
			window.location.assign(logoutData.logout_url);
		} catch (error) {
			setLoginError(formatAuthError(error, "Unable to switch Google account."));
			isOAuthSubmittingRef.current = false;
			setIsOAuthSubmitting(false);
		}
	}, [formatAuthError]);

	async function onOAuthLogin() {
		if (isAuthMethodsLoading) {
			setLoginError("Sign-in methods are loading. Please wait a moment.");
			return;
		}

		if (!authMethods.oauth_enabled) {
			setLoginError("Google OAuth sign-in is disabled by administrator.");
			return;
		}

		if (isOAuthSubmittingRef.current) {
			return;
		}

		if (typeof window !== "undefined") {
			window.sessionStorage.removeItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY);
			window.sessionStorage.removeItem(OAUTH_UNAUTHORIZED_ERROR_KEY);
			window.sessionStorage.removeItem(OAUTH_EXPLORE_MISMATCH_ERROR_KEY);
		}

		const shouldForceChooserOnce =
			typeof window !== "undefined" && window.sessionStorage.getItem(FORCE_ACCOUNT_CHOOSER_ONCE_KEY) === "1";
		if (shouldForceChooserOnce && typeof window !== "undefined") {
			window.sessionStorage.removeItem(FORCE_ACCOUNT_CHOOSER_ONCE_KEY);
			await startOAuthLoginAfterProviderLogout();
			return;
		}

		const rememberedEmail =
			typeof window !== "undefined"
				? String(window.localStorage.getItem(LAST_LOGOUT_EMAIL_KEY) || "").trim().toLowerCase()
				: "";

		await startOAuthLoginFlow({
			forceAccountSelection: shouldForceChooserOnce,
			forceReauthentication: false,
			loginHint: shouldForceChooserOnce ? "" : rememberedEmail,
		});
	}

	async function onUseAnotherGoogleAccount() {
		if (isOAuthSubmittingRef.current) {
			return;
		}

		const hasActiveSession = Boolean(
			String(getAuthToken() || "").trim() ||
				String(getAuthIdToken() || "").trim() ||
				String(getAuthRefreshToken() || "").trim()
		);
		if (!hasActiveSession) {
			await startOAuthLoginAfterProviderLogout();
			return;
		}

		try {
			isOAuthSubmittingRef.current = true;
			setIsOAuthSubmitting(true);
			setLoginError("");
			setOauthRetryAvailable(false);
			setLoginEmail("");
			setLoginPassword("");

			if (typeof window !== "undefined") {
				window.localStorage.removeItem(LAST_LOGOUT_EMAIL_KEY);
				window.sessionStorage.removeItem(FORCE_ACCOUNT_CHOOSER_ONCE_KEY);
				window.sessionStorage.removeItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY);
				window.sessionStorage.removeItem(ADD_ACCOUNT_RETURN_PATH_KEY);
				window.sessionStorage.removeItem(OAUTH_UNAUTHORIZED_ERROR_KEY);
				window.sessionStorage.removeItem(OAUTH_EXPLORE_MISMATCH_ERROR_KEY);
			}

			const redirectUri = `${window.location.origin}`;
			const pkcePair = await generatePkcePair();
			window.sessionStorage.setItem("rfm_oauth_redirect_uri", redirectUri);
			window.sessionStorage.setItem(PKCE_VERIFIER_KEY, pkcePair.verifier);

			const logoutData = await getOAuthLogoutUrl(redirectUri, {
				idTokenHint: String(getAuthIdToken() || "").trim(),
				forceAccountSelection: true,
				forceReauthentication: true,
				codeChallenge: pkcePair.challenge,
				codeChallengeMethod: "S256",
			});

			const state = (logoutData?.state || "").trim();
			if (state) {
				window.sessionStorage.setItem("rfm_oauth_state", state);
			}
			if (!logoutData?.logout_url) {
				throw new Error("OAuth logout URL not returned by backend");
			}
			window.location.assign(logoutData.logout_url);
		} catch (error) {
			setLoginError(formatAuthError(error, "Unable to switch Google account."));
			isOAuthSubmittingRef.current = false;
			setIsOAuthSubmitting(false);
		}
	}

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		if (location.pathname !== "/" && location.pathname !== "/callback") {
			return;
		}

		const params = new URLSearchParams(location.search || "");
		const hasOAuthPayload = Boolean((params.get("code") || "").trim() || (params.get("error") || "").trim());
		if (hasOAuthPayload) {
			return;
		}

		const hasPendingAddAccount = window.sessionStorage.getItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY) === "1";
		if (hasPendingAddAccount) {
			window.sessionStorage.removeItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY);
			window.sessionStorage.removeItem(ADD_ACCOUNT_RETURN_PATH_KEY);
			isOAuthSubmittingRef.current = false;
			setIsOAuthSubmitting(false);

			navigate("/", { replace: true });
			startOAuthLoginFlow({
				forceAccountSelection: true,
				forceReauthentication: true,
				loginHint: "",
			});
			return;
		}

		if (window.sessionStorage.getItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY) === "1" || window.sessionStorage.getItem(ADD_ACCOUNT_RETURN_PATH_KEY)) {
			window.sessionStorage.removeItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY);
			window.sessionStorage.removeItem(ADD_ACCOUNT_RETURN_PATH_KEY);
			isOAuthSubmittingRef.current = false;
			setIsOAuthSubmitting(false);
		}
	}, [location.pathname, location.search, navigate, startOAuthLoginFlow]);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		if (location.pathname !== "/" && location.pathname !== "/callback") {
			return;
		}

		if (window.sessionStorage.getItem(OAUTH_UNAUTHORIZED_ERROR_KEY) !== "1") {
			if (window.sessionStorage.getItem(OAUTH_EXPLORE_MISMATCH_ERROR_KEY) !== "1") {
				return;
			}

			window.sessionStorage.removeItem(OAUTH_EXPLORE_MISMATCH_ERROR_KEY);
			window.sessionStorage.setItem(FORCE_ACCOUNT_CHOOSER_ONCE_KEY, "1");
			const message = "The selected Google account does not match the explore form email. Use the same Google account to continue.";
			if (getExploreTrialId()) {
				window.sessionStorage.setItem(EXPLORE_LOGIN_ERROR_KEY, message);
				navigate("/explore", { replace: true });
				return;
			}
			setOauthRetryAvailable(true);
			setLoginError(message);
			return;
		}

		window.sessionStorage.removeItem(OAUTH_UNAUTHORIZED_ERROR_KEY);
		setOauthRetryAvailable(true);
		setLoginError("This email is not authorized");
	}, [location.pathname, location.search, navigate]);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		const pathname = location.pathname;
		const search = location.search || "";

		if (pathname === "/") {
			const rootParams = new URLSearchParams(search);
			const hasOAuthPayload = Boolean((rootParams.get("code") || "").trim() || (rootParams.get("error") || "").trim());
			if (!hasOAuthPayload) {
				return;
			}
		}

		if (pathname !== "/callback" && pathname !== "/") {
			return;
		}

		const params = new URLSearchParams(search);
		const code = (params.get("code") || "").trim();
		const error = (params.get("error") || "").trim();
		const state = (params.get("state") || "").trim();
		const expectedState = (window.sessionStorage.getItem("rfm_oauth_state") || "").trim();
		const hasPkceVerifier = Boolean((window.sessionStorage.getItem(PKCE_VERIFIER_KEY) || "").trim());
		const lockedCode = (window.sessionStorage.getItem(OAUTH_EXCHANGE_LOCK_KEY) || "").trim();
		const lastHandledCode = (window.sessionStorage.getItem(OAUTH_LAST_CODE_KEY) || "").trim();
		const returnToExploreWithError = (message) => {
			if (!getExploreTrialId()) {
				return false;
			}
			window.sessionStorage.setItem(EXPLORE_LOGIN_ERROR_KEY, message);
			isOAuthSubmittingRef.current = false;
			setIsOAuthSubmitting(false);
			navigate("/explore", { replace: true });
			return true;
		};

		if (error) {
			if (returnToExploreWithError(`OAuth login failed: ${error}`)) {
				return;
			}
			isOAuthSubmittingRef.current = false;
			setIsOAuthSubmitting(false);
			setLoginError(`OAuth login failed: ${error}`);
			navigate("/", { replace: true });
			return;
		}

		if (!code) {
			if (returnToExploreWithError("OAuth login failed: missing authorization code.")) {
				return;
			}
			isOAuthSubmittingRef.current = false;
			setIsOAuthSubmitting(false);
			setLoginError("OAuth login failed: missing authorization code.");
			navigate("/", { replace: true });
			return;
		}

		if (!expectedState || !hasPkceVerifier) {
			// Ignore stale OAuth callback params (for example after local logout)
			// and keep the user on the login page without forcing a token exchange.
			if (getExploreTrialId()) {
				navigate("/explore", { replace: true });
				return;
			}
			navigate("/", { replace: true });
			return;
		}

		if (
			oauthExchangeInFlightRef.current ||
			lastHandledOAuthCodeRef.current === code ||
			lockedCode === code ||
			lastHandledCode === code
		) {
			navigate("/", { replace: true });
			return;
		}

		if (expectedState && state && expectedState !== state) {
			if (returnToExploreWithError("OAuth login failed: state mismatch.")) {
				return;
			}
			isOAuthSubmittingRef.current = false;
			setIsOAuthSubmitting(false);
			setLoginError("OAuth login failed: state mismatch.");
			navigate("/", { replace: true });
			return;
		}

		// Consume callback URL immediately so the same authorization code
		// is not retried by browser/dev re-renders.
		navigate("/", { replace: true });

		(async () => {
			try {
				oauthExchangeInFlightRef.current = true;
				lastHandledOAuthCodeRef.current = code;
				window.sessionStorage.setItem(OAUTH_EXCHANGE_LOCK_KEY, code);
				window.sessionStorage.setItem(OAUTH_LAST_CODE_KEY, code);
				isOAuthSubmittingRef.current = true;
				setIsOAuthSubmitting(true);
				const redirectUri =
					(window.sessionStorage.getItem("rfm_oauth_redirect_uri") || "").trim() ||
					`${window.location.origin}`;
				const codeVerifier = (window.sessionStorage.getItem(PKCE_VERIFIER_KEY) || "").trim();
				if (!codeVerifier) {
					throw new Error("OAuth login failed: PKCE verifier missing");
				}
				const exploreTrialId = getExploreTrialId() || null;
				const result = await exchangeOAuthCode(code, redirectUri, codeVerifier, exploreTrialId);
				if (result?.is_authorized === false) {
					if (typeof window !== "undefined") {
						window.sessionStorage.setItem(OAUTH_UNAUTHORIZED_ERROR_KEY, "1");
						window.sessionStorage.removeItem("rfm_auth_token");
						window.sessionStorage.removeItem("rfm_auth_id_token");
						window.sessionStorage.removeItem("rfm_auth_refresh_token");
						window.localStorage.removeItem("rfm_auth_token");
						window.localStorage.removeItem("rfm_auth_id_token");
						window.localStorage.removeItem("rfm_auth_refresh_token");
					}

					setOauthRetryAvailable(true);
					window.sessionStorage.removeItem("rfm_oauth_redirect_uri");
					window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
					clearExploreTrialId();
					window.sessionStorage.removeItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY);
					window.sessionStorage.removeItem(ADD_ACCOUNT_RETURN_PATH_KEY);
					window.sessionStorage.removeItem(OAUTH_EXCHANGE_LOCK_KEY);

					if (result?.logout_url) {
						window.location.assign(result.logout_url);
						return;
					}

					setLoginError("This email is not authorized");
					return;
				}

				if (typeof window !== "undefined") {
					window.sessionStorage.removeItem(OAUTH_UNAUTHORIZED_ERROR_KEY);
				}
				setOauthRetryAvailable(false);
				setAuthProfile(result?.profile || null);
				if (typeof window !== "undefined" && result?.profile?.email) {
					window.localStorage.setItem(LAST_LOGOUT_EMAIL_KEY, normalizeAccountEmail(result.profile.email));
				}
				setPasswordChangeRequired(Boolean(result?.profile?.password_change_required));
				sessionExpiredHandledRef.current = false;
				setIsAuthenticated(true);
				const pendingTrialPhone = (window.sessionStorage.getItem(TRIAL_PREFILL_PHONE_KEY) || "").trim();
				const pendingTrialName = (window.sessionStorage.getItem(TRIAL_PREFILL_NAME_KEY) || "").trim();
				if (pendingTrialPhone) {
					try {
						await saveTrialPhone(pendingTrialPhone, pendingTrialName);
						setShowPhoneCaptureModal(false);
					} catch (trialSaveError) {
						console.error("[Trial] Failed to auto-save prefilled phone:", trialSaveError?.message || trialSaveError);
					}
				}
				const shouldChangePassword = Boolean(result?.profile?.password_change_required);
				const hasAddAccountFlow =
					window.sessionStorage.getItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY) === "1" ||
					window.sessionStorage.getItem(ADD_ACCOUNT_RETURN_PATH_KEY);

				if (exploreTrialId) {
					navigate("/analysis", { replace: true });
				} else if (!shouldChangePassword && hasAddAccountFlow) {
					const returnPath =
						(window.sessionStorage.getItem(ADD_ACCOUNT_RETURN_PATH_KEY) || "").trim() ||
						"/analysis";
					navigate(returnPath, { replace: true });
				} else {
					navigate(shouldChangePassword ? "/change-password" : "/onboarding", { replace: true });
				}
				setIsLoading(true);
				setLoginError("");
				window.sessionStorage.removeItem("rfm_oauth_state");
				window.sessionStorage.removeItem("rfm_oauth_redirect_uri");
				window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
				if (!exploreTrialId) {
					clearExploreTrialId();
				}
				window.sessionStorage.removeItem(TRIAL_PREFILL_PHONE_KEY);
				window.sessionStorage.removeItem(TRIAL_PREFILL_NAME_KEY);
				window.sessionStorage.removeItem(FORCE_ACCOUNT_CHOOSER_ONCE_KEY);
				window.sessionStorage.removeItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY);
				window.sessionStorage.removeItem(ADD_ACCOUNT_RETURN_PATH_KEY);
				window.sessionStorage.removeItem(OAUTH_UNAUTHORIZED_ERROR_KEY);
				window.sessionStorage.removeItem(OAUTH_EXCHANGE_LOCK_KEY);
				return;
			} catch (oauthError) {
				const statusCode = Number(oauthError?.response?.status || 0);
				const detailText = String(
					oauthError?.response?.data?.detail || oauthError?.message || ""
				).toLowerCase();
				const exploreTrialIdAtFailure = getExploreTrialId() || null;
				const isEmailNotAllowed =
					statusCode === 402 ||
					(detailText.includes("email") && detailText.includes("not allowed"));
				const isExploreAccountFlowError =
					detailText.includes("explore account") ||
					(detailText.includes("explore") && detailText.includes("sign-in flow"));
				const isExploreEmailMismatch =
					detailText.includes("explore login email does not match") ||
					(detailText.includes("explore") && detailText.includes("email") && detailText.includes("does not match"));
				const canRetryWithAnotherGoogleAccount =
					isEmailNotAllowed || isExploreAccountFlowError || isExploreEmailMismatch;

				if (canRetryWithAnotherGoogleAccount && typeof window !== "undefined") {
					window.localStorage.removeItem(LAST_LOGOUT_EMAIL_KEY);
					if (isExploreEmailMismatch) {
						window.sessionStorage.setItem(OAUTH_EXPLORE_MISMATCH_ERROR_KEY, "1");
						window.sessionStorage.removeItem(OAUTH_UNAUTHORIZED_ERROR_KEY);
						window.sessionStorage.setItem(FORCE_ACCOUNT_CHOOSER_ONCE_KEY, "1");
					} else {
						window.sessionStorage.setItem(OAUTH_UNAUTHORIZED_ERROR_KEY, "1");
						window.sessionStorage.removeItem(OAUTH_EXPLORE_MISMATCH_ERROR_KEY);
					}
				}
				if (canRetryWithAnotherGoogleAccount) {
					setOauthRetryAvailable(true);
				}

				const nextLoginError =
					isExploreEmailMismatch
						? "The selected Google account does not match the explore form email. Use another Google account to continue."
						: isExploreAccountFlowError
						? "This Google account belongs to an explore account. Open /explore to continue, or use another Google account."
						: isEmailNotAllowed
						? "This email is not authorized"
						: formatAuthError(oauthError, "OAuth login failed. Please retry Google sign-in.");

				if (exploreTrialIdAtFailure) {
					window.sessionStorage.setItem(EXPLORE_LOGIN_ERROR_KEY, nextLoginError);
					navigate("/explore", { replace: true });
				} else {
					setLoginError(nextLoginError);
				}
				window.sessionStorage.removeItem("rfm_oauth_redirect_uri");
				window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
				if (!isExploreEmailMismatch) {
					window.sessionStorage.removeItem(FORCE_ACCOUNT_CHOOSER_ONCE_KEY);
				}
				window.sessionStorage.removeItem(TRIAL_PREFILL_PHONE_KEY);
				window.sessionStorage.removeItem(TRIAL_PREFILL_NAME_KEY);
				window.sessionStorage.removeItem(ADD_ACCOUNT_AFTER_LOGOUT_KEY);
				window.sessionStorage.removeItem(ADD_ACCOUNT_RETURN_PATH_KEY);
				window.sessionStorage.removeItem(OAUTH_EXCHANGE_LOCK_KEY);
				if (!canRetryWithAnotherGoogleAccount) {
					window.sessionStorage.removeItem(OAUTH_UNAUTHORIZED_ERROR_KEY);
					window.sessionStorage.removeItem(OAUTH_EXPLORE_MISMATCH_ERROR_KEY);
				}
			} finally {
				oauthExchangeInFlightRef.current = false;
				isOAuthSubmittingRef.current = false;
				setIsOAuthSubmitting(false);
			}
		})();
	}, [formatAuthError, location.pathname, location.search, navigate, startOAuthLoginFlow]);

	const loginView = (
			<div className="login-screen">
				<div className="login-card enterprise" role="dialog" aria-labelledby="login-title">
					<div className="login-hero-side" aria-hidden="true">
						<div
							className="login-hero-slider-track"
							style={{ transform: `translateX(-${activeHeroSlide * 100}%)` }}
						>
							{LOGIN_HERO_SLIDES.map((slide) => (
								<div
									key={slide.image}
									className="login-hero-slide"
									style={{ backgroundImage: `url(${slide.image})` }}
								/>
							))}
						</div>
						<div className="login-hero-overlay" />
						<div className="login-hero-grid" />
						<div className="login-hero-copy">
							<p className="login-kicker">{currentHeroSlide.kicker}</p>
							<h2>{currentHeroSlide.title}</h2>
							<p>{currentHeroSlide.description}</p>
							<div className="login-hero-points">
								{(currentHeroSlide.points || []).map((point) => (
									<span key={point} className="login-hero-point">{point}</span>
								))}
							</div>
						</div>
						<div className="login-hero-dots">
							{LOGIN_HERO_SLIDES.map((slide, index) => (
								<span
									key={slide.image}
									className={`login-hero-dot ${index === activeHeroSlide ? "active" : ""}`}
								/>
							))}
						</div>
					</div>
						<div className="login-form-side">
							<div className="login-brand-chip">KORVRFM</div>
							<h1 id="login-title">Sign in to KORVRFM</h1>
							<p className="login-subtext">One platform for segmentation, retention, and growth in every domain.</p>
							{isAuthMethodsLoading ? (
								<AppLoader
									variant="inline"
									title="Preparing Sign-In"
									message="Loading available sign-in methods..."
								/>
							) : null}
						{!isAuthMethodsLoading && authMethods.password_enabled ? (
					<form className="login-form" onSubmit={onLoginSubmit} autoComplete="off">
						<input
							type="text"
							name="prevent_autofill_username"
							autoComplete="username"
							tabIndex={-1}
							aria-hidden="true"
							style={{ display: "none" }}
						/>
						<input
							type="password"
							name="prevent_autofill_password"
							autoComplete="new-password"
							tabIndex={-1}
							aria-hidden="true"
							style={{ display: "none" }}
						/>
						<input
							type="email"
							name="login_email"
							value={loginEmail}
							onChange={(event) => setLoginEmail(event.target.value)}
							placeholder="Email"
							autoComplete="off"
							autoCapitalize="none"
							autoCorrect="off"
							spellCheck={false}
							autoFocus
							required
						/>
						<input
							type="password"
							name="login_password"
							value={loginPassword}
							onChange={(event) => setLoginPassword(event.target.value)}
							placeholder="Password"
							autoComplete="new-password"
							required
						/>
						<button type="submit" disabled={isLoginSubmitting}>
							{isLoginSubmitting ? "Signing in..." : "Login"}
						</button>
					</form>
						) : null}
						{!isAuthMethodsLoading && authMethods.password_enabled && authMethods.oauth_enabled ? (
					<div className="login-sso-divider" role="separator" aria-label="Sign in with">
						<span>Sign in with</span>
					</div>
						) : null}
						{!isAuthMethodsLoading && authMethods.oauth_enabled ? (
						<>
							<button
								type="button"
								className="oauth-login-btn enterprise-google"
								onClick={onOAuthLogin}
								disabled={isOAuthSubmitting}
							>
								<span className="oauth-google-icon" aria-hidden="true">
									<svg viewBox="0 0 48 48">
										<path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.24 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.956 3.044l5.657-5.657C34.046 6.053 29.28 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
										<path fill="#FF3D00" d="M6.307 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.956 3.044l5.657-5.657C34.046 6.053 29.28 4 24 4c-7.682 0-14.348 4.337-17.693 10.691z"/>
										<path fill="#4CAF50" d="M24 44c5.177 0 9.86-1.978 13.409-5.192l-6.193-5.238C29.146 35.091 26.684 36 24 36c-5.219 0-9.619-3.318-11.283-7.946l-6.522 5.026C9.5 39.556 16.242 44 24 44z"/>
										<path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.57l.003-.002 6.193 5.238C36.971 39.329 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
									</svg>
								</span>
								<span className="oauth-login-copy">
									<strong>{isOAuthSubmitting ? "Redirecting..." : "Sign in with Google"}</strong>
								</span>
								<span className="oauth-login-arrow" aria-hidden="true">&#8594;</span>
							</button>
							{authMethods.show_use_another_google_account !== false || oauthRetryAvailable ? (
								<button
									type="button"
									className="oauth-login-btn enterprise-google secondary"
									onClick={onUseAnotherGoogleAccount}
									disabled={isOAuthSubmitting}
								>
									<span className="oauth-login-copy">
										<strong>Use another Google account</strong>
									</span>
								</button>
							) : null}
						</>
						) : null}
					</div>
				</div>
			</div>
		);

	const developerView = (
			<div className="app-shell">
				<DeveloperDashboard
					onNotify={showToast}
					onBack={() => navigate("/onboarding")}
					onLogout={onLogout}
					currentUserEmail={currentAccountEmail}
					onCurrentUserUpdated={async () => {
						const refreshedProfile = await getCurrentUserProfile();
						setAuthProfile(refreshedProfile || null);
					}}
				/>
				<Toast toast={toast} onClose={() => setToast({ message: "", type: "info" })} />
			</div>
		);

	const onboardingView = (
			<div className="org-hub-screen">
				<button className="access-logout-btn" onClick={onLogout}>
					<span className="logout-btn-icon" aria-hidden="true">
						<svg viewBox="0 0 20 20">
							<path d="M8 3h-3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3" />
							<path d="M13 6l4 4-4 4" />
							<path d="M17 10H8" />
						</svg>
					</span>
					<span>Logout</span>
				</button>
				<div className="org-hub-shell">
					{isRoleResolved && (isAdmin || canManageEmployees) ? (
						<div className="org-hub-admin-bar">
							{isAdmin ? (
								<button
									type="button"
									className="org-hub-secondary-btn"
									onClick={() => navigate("/admin")}
								>
									Open Admin Dashboard
								</button>
							) : null}
							{canManageEmployees ? (
								<button
									type="button"
									className="org-hub-secondary-btn"
									onClick={() => navigate("/team")}
								>
									Open Team Dashboard
								</button>
							) : null}
						</div>
					) : null}
					<div className="org-hub-hero">
						<p className="org-hub-kicker">KorvRFM Workspace</p>
						<h1>
							{!isRoleResolved
								? "Restoring your workspace"
								: hasSingleOrganization
								? "Entering your organization"
								: canRequestOrganization
								? "Request access to a new organization"
								: "Choose Your Organization Path"}
						</h1>
						<p>
							{!isRoleResolved
								? "We are loading your account role and organization access."
								: isOrganizationsLoading
								? "Loading organization access..."
								: hasOrganizations
								? "Select an existing organization to continue to analysis."
								: canRequestOrganization
								? "Request access from your client manager or submit a new organization request for approval."
								: "No organization is assigned yet. Contact your client manager to get access to an organization."}
						</p>
					</div>

					<div className="org-hub-path-actions">
						{!isRoleResolved ? (
							<div className="org-path-btn existing" aria-hidden="true">
								<span className="org-path-copy">
									<strong>Loading your access...</strong>
									<small>Please wait while we restore your role and organizations</small>
								</span>
							</div>
						) : isOrganizationsLoading ? (
							<div className="org-path-btn existing" aria-hidden="true">
								<span className="org-path-copy">
									<strong>Loading...</strong>
									<small>Please wait while we fetch your organizations</small>
								</span>
							</div>
						) : null}
						{isRoleResolved && hasOrganizations ? (
							<button
								type="button"
								className="org-path-btn existing"
								onClick={() => openOnboardingPopup("existing")}
							>
								<span className="org-path-icon" aria-hidden="true">
									<svg viewBox="0 0 24 24">
										<path d="M4 20h16" />
										<path d="M6 20V8l6-4 6 4v12" />
										<path d="M9 12h6" />
									</svg>
								</span>
								<span className="org-path-copy">
									<strong>Existing Organization</strong>
									<small>Resume analysis for your approved organizations</small>
								</span>
							</button>
						) : null}

						{isRoleResolved && canStartFreshOrganization ? (
							<button
								type="button"
								className="org-path-btn fresh"
								onClick={() => openOnboardingPopup("new")}
							>
								<span className="org-path-icon" aria-hidden="true">
									<svg viewBox="0 0 24 24">
										<path d="M12 5v14" />
										<path d="M5 12h14" />
										<rect x="3.5" y="3.5" width="17" height="17" rx="4" />
									</svg>
								</span>
								<span className="org-path-copy">
									<strong>Start Fresh Organization</strong>
									<small>Create a new business path for import</small>
								</span>
							</button>
						) : null}

						{isRoleResolved && canRequestOrganization ? (
							<button
								type="button"
								className="org-path-btn fresh"
								onClick={() => openOnboardingPopup("request")}
							>
								<span className="org-path-icon" aria-hidden="true">
									<svg viewBox="0 0 24 24">
										<path d="M12 5v14" />
										<path d="M5 12h14" />
										<rect x="3.5" y="3.5" width="17" height="17" rx="4" />
									</svg>
								</span>
								<span className="org-path-copy">
									<strong>Request New Organization</strong>
									<small>Submit for manager/admin approval before import</small>
								</span>
							</button>
						) : null}

						{isRoleResolved && !hasOrganizations && !canStartFreshOrganization && !canRequestOrganization ? (
							<div className="org-access-empty-state">
								<strong>Organization access is not assigned yet.</strong>
								<span>Contact your client manager who created this employee account and ask them to grant access to the right organization.</span>
							</div>
						) : null}
					</div>
				</div>

				{onboardingPopup === "existing" ? (
					<div className="org-popup-overlay" onClick={closeOnboardingPopup}>
						<div className="org-popup-card" onClick={(event) => event.stopPropagation()}>
							<div className="org-popup-head">
								<h3>Existing Organization</h3>
								<button type="button" className="org-popup-close" onClick={closeOnboardingPopup}>
									Close
								</button>
							</div>
							<p className="org-popup-sub">Select a business and continue to analysis.</p>
							<div className="org-domain-picker">
								<label>
									<span>Sector</span>
									<select
										value={activeOnboardingDomainKey}
										onChange={(event) => {
											const nextDomainKey = event.target.value;
											setOnboardingDomainKey(nextDomainKey);
											const nextGroup = organizationDomainGroups.find((group) => group.key === nextDomainKey);
											const firstOrgId = nextGroup?.organizations?.[0]?.org_id || "";
											if (firstOrgId) {
												onOnboardingOrgChange(firstOrgId);
											}
										}}
									>
										{organizationDomainGroups.map((group) => (
											<option key={group.key} value={group.key}>
												{group.label} ({group.organizations.length})
											</option>
										))}
									</select>
								</label>
							</div>
							<select
								value={selectedOrgId}
								onChange={(event) => {
									const nextOrgId = event.target.value;
									onOnboardingOrgChange(nextOrgId);
									setOnboardingDomainKey(findOrganizationDomainKey(organizations, nextOrgId));
								}}
							>
								<option value="">Select organization</option>
								{onboardingDomainOrganizations.map((org) => (
									<option key={org.org_id} value={org.org_id}>
										{formatTitleCase(org.name)} - {getOrganizationDomainLabel(org)}
									</option>
								))}
							</select>
							<div className="org-popup-actions">
								<button
									type="button"
									className="org-hub-primary-btn with-icon"
									onClick={onGoToAnalysis}
									disabled={!canGoToAnalysis}
								>
									<span className="org-btn-icon" aria-hidden="true">
										<svg viewBox="0 0 20 20">
											<path d="M6 4l7 6-7 6" />
										</svg>
									</span>
									<span>Go to Analysis</span>
								</button>
								<button
									type="button"
									className={`org-report-action-btn${isGeneratingOrgReport ? " is-working" : ""}`}
									onClick={openReportContactPrompt}
									disabled={!selectedOrgId || isGeneratingOrgReport}
								>
									<span className="org-report-icon" aria-hidden="true">
										<svg viewBox="0 0 20 20">
											<path d="M5 3h7l3 3v11H5z" />
											<path d="M12 3v4h4" />
											<path d="M7.5 11h5" />
											<path d="M7.5 14h4" />
										</svg>
									</span>
									<span>{isGeneratingOrgReport ? "Generating Report" : "Generate Report"}</span>
								</button>
							</div>
							<div className="org-popup-import-block">
								<p className="org-popup-import-title">Import Data For Selected Organization</p>
								<p className="org-popup-sub">
									Uploaded file will be processed under the selected organization.
								</p>
								<EtlPipelinePanel
									onNotify={showToast}
									onComplete={onPipelineComplete}
									selectedOrgId={selectedOrgId}
									selectedOrgName={selectedOrgName}
									stagedNewOrgName=""
									onOrganizationCreated={onImportOrgCreated}
								/>
							</div>
						</div>
					</div>
				) : null}

				{onboardingPopup === "new" ? (
					<div className="org-popup-overlay" onClick={closeOnboardingPopup}>
						<div className="org-popup-card accent" onClick={(event) => event.stopPropagation()}>
							<div className="org-popup-head">
								<h3>Start Fresh Organization</h3>
								<button type="button" className="org-popup-close" onClick={closeOnboardingPopup}>
									Close
								</button>
							</div>
							<p className="org-popup-sub">Create a new organization and import data to activate analytics.</p>
							<div className="org-hub-create-row">
								<input
									type="text"
									value={newOrganizationName}
									onChange={(event) => setNewOrganizationName(event.target.value)}
									placeholder="Enter organization name"
								/>
							</div>
							{newOrganizationName.trim() && !canGoToAnalysis ? (
								<p className="org-hub-note">
									New organization "{newOrganizationName.trim()}" is staged. It will be created
									in database only after data upload starts.
								</p>
							) : null}
							<div className="org-popup-import-block">
								<p className="org-popup-import-title">Import Data And Create Organization</p>
								<p className="org-popup-sub">
									When upload starts, this organization will be created automatically.
								</p>
								<EtlPipelinePanel
									onNotify={showToast}
									onComplete={onPipelineComplete}
									selectedOrgId=""
									selectedOrgName=""
									stagedNewOrgName={newOrganizationName.trim()}
									onOrganizationCreated={onImportOrgCreated}
								/>
							</div>
						</div>
					</div>
				) : null}

				{onboardingPopup === "request" ? (
					<div className="org-popup-overlay" onClick={closeOnboardingPopup}>
						<div className="org-popup-card accent" onClick={(event) => event.stopPropagation()}>
							<div className="org-popup-head">
								<h3>Request New Organization</h3>
								<button type="button" className="org-popup-close" onClick={closeOnboardingPopup}>
									Close
								</button>
							</div>
							<p className="org-popup-sub">Enter organization name. Your manager/admin must approve before you can import new data for it.</p>
							<div className="org-hub-create-row">
								<input
									type="text"
									value={requestOrganizationName}
									onChange={(event) => setRequestOrganizationName(event.target.value)}
									placeholder="Enter organization name"
								/>
							</div>
							<div className="org-popup-actions">
								<button
									type="button"
									className="org-hub-primary-btn with-icon"
									onClick={onSubmitOrgRequest}
									disabled={isSubmittingOrgRequest || !requestOrganizationName.trim()}
								>
									<span className="org-btn-icon" aria-hidden="true">
										<svg viewBox="0 0 20 20">
											<path d="M6 4l7 6-7 6" />
										</svg>
									</span>
									<span>{isSubmittingOrgRequest ? "Submitting..." : "Submit Request"}</span>
								</button>
							</div>
						</div>
					</div>
				) : null}
				{isReportContactOpen ? (
					<div className="org-popup-overlay" onClick={() => setIsReportContactOpen(false)}>
						<form className="org-report-contact-modal" onSubmit={onConfirmReportContact} onClick={(event) => event.stopPropagation()}>
							<div className="org-popup-head">
								<div>
									<h3>Report Contact Details</h3>
									<p className="org-popup-sub">
										{reportContactForm.contact_person_name.trim()
											? "Confirm these details or update them before generating the report."
											: "Add the contact details before generating the report."}
									</p>
								</div>
								<button type="button" className="org-popup-close" onClick={() => setIsReportContactOpen(false)}>
									Close
								</button>
							</div>
							<div className="org-report-contact-grid">
								<label>
									<span>Contact Person Name</span>
									<input
										type="text"
										value={reportContactForm.contact_person_name}
										onChange={(event) =>
											setReportContactForm((current) => ({
												...current,
												contact_person_name: event.target.value,
											}))
										}
										placeholder="Enter contact person name"
										required
									/>
								</label>
								<label>
									<span>Phone Number</span>
									<input
										type="tel"
										value={reportContactForm.contact_phone}
										onChange={(event) =>
											setReportContactForm((current) => ({
												...current,
												contact_phone: event.target.value,
											}))
										}
										placeholder="Enter phone number"
									/>
								</label>
								<label className="span-2">
									<span>Address</span>
									<textarea
										value={reportContactForm.contact_address}
										onChange={(event) =>
											setReportContactForm((current) => ({
												...current,
												contact_address: event.target.value,
											}))
										}
										placeholder="Enter address"
										rows={3}
									/>
								</label>
							</div>
							<div className="org-popup-actions">
								<button type="button" className="org-popup-close" onClick={() => setIsReportContactOpen(false)}>
									Cancel
								</button>
								<button
									type="submit"
									className="org-report-action-btn"
									disabled={!reportContactForm.contact_person_name.trim() || isGeneratingOrgReport}
								>
									<span>{isGeneratingOrgReport ? "Generating Report" : "Confirm & Generate"}</span>
								</button>
							</div>
						</form>
					</div>
				) : null}
				{orgReportUrl ? (
					<div className="org-report-modal-overlay" onClick={clearGeneratedOrgReport}>
						<div className="org-report-modal" onClick={(event) => event.stopPropagation()}>
							<div className="org-report-modal-head">
								<div>
									<strong>Customer Portfolio Report</strong>
									<small>
										{formatTitleCase(selectedOrgName || "Selected Organization")} ·{" "}
										{REPORT_LANGUAGE_LABEL}
									</small>
								</div>
								<div className="org-report-modal-actions">
									<button type="button" className="org-report-download" onClick={onPrintOrgReport}>
										Print / Save PDF
									</button>
									<button type="button" className="org-popup-close" onClick={clearGeneratedOrgReport}>
										Close
									</button>
								</div>
							</div>
							<iframe ref={orgReportFrameRef} title="Customer portfolio report preview" srcDoc={orgReportHtml} />
						</div>
					</div>
				) : null}
				<Toast toast={toast} onClose={() => setToast({ message: "", type: "info" })} />
			</div>
		);

	const shouldShowAnalysisLoader = isLoading || !isAnalysisDataReady;
	const analysisView = (
		<div className={`app-shell analysis-shell${isEmbeddedMode ? " embedded-analysis-shell" : ""}`}>
			{!isEmbeddedMode ? (
				<Header
					organizations={organizations}
					selectedOrgId={selectedOrgId}
					onOrgChange={onOrgSelectionChange}
					onOpenDeveloperDashboard={isAdmin ? () => navigate("/admin") : null}
					onOpenTeamDashboard={canManageEmployees ? () => navigate("/team") : null}
					onConnectWhatsApp={canUseWhatsApp ? onConnectWhatsApp : null}
					onDisconnectWhatsApp={canUseWhatsApp ? onDisconnectWhatsApp : null}
					onLogout={onLogout}
					userDisplayName={profileDisplayName}
					userEmail={currentAccountEmail}
					userRole={authProfile?.role}
					canManageEmployees={canManageEmployees}
					whatsappConnection={whatsappConnection}
					isWhatsAppConnecting={isWhatsAppConnecting}
					isWhatsAppDisconnecting={isWhatsAppDisconnecting}
				/>
			) : null}

			{!shouldShowAnalysisLoader ? (
				<main className="main-content">
						<KpiGrid
							kpis={kpis}
							segmentSummary={segmentSummary}
							customers={customers}
							terminology={selectedOrgTerminology}
							selectedMetric={selectedDashboardMetric}
							onSelectedMetricChange={setSelectedDashboardMetric}
								onTakeAction={onTakeSegmentAction}
						/>

						<CustomerTable
						customers={customers}
						selectedSegments={selectedSegments}
						onSelectedSegmentsChange={setSelectedSegments}
						sortKey={sortKey}
						sortDirection={sortDirection}
						onSort={onSort}
						selectedCustomerId={selectedCustomerId}
							onSelectCustomer={handleSelectCustomer}
						selectedCustomer={selectedCustomer}
						timeline={timeline}
						isTimelineLoading={isTimelineLoading}
						terminology={selectedOrgTerminology}
					/>

					<ChartsSection
						revenueTrend={revenueTrend}
						topProducts={topProducts}
						selectedCustomerId={selectedCustomerId}
						terminology={selectedOrgTerminology}
					/>
				</main>
			) : null}

			{shouldShowAnalysisLoader && (
				<AppLoader title="Loading Dashboard" message="Bringing your analytics into view..." />
			)}

				<SegmentActionModal
					context={segmentActionContext}
					customers={customers}
					organizationName={selectedOrgName || organizationName}
					terminology={selectedOrgTerminology}
					templates={segmentMessageTemplates}
					whatsappConnection={whatsappConnection}
					whatsappTemplates={whatsappTemplates}
					whatsappTemplateLoadError={whatsappTemplateLoadError}
					whatsappAnalytics={whatsappAnalytics}
					suggestiveActionsConfig={suggestiveActionsConfig}
					onClose={() => setSegmentActionContext(null)}
					onRunAction={onRunSegmentAction}
					onSendWhatsAppCampaign={onSendWhatsAppCampaign}
					onCreateWhatsAppTemplateRequest={onCreateWhatsAppTemplateRequest}
					onResetTemplate={onResetSegmentTemplate}
					onCreateSuggestiveAction={onCreateSuggestiveAction}
					onUpdateSuggestiveAction={onUpdateSuggestiveAction}
					onToggleSuggestiveAction={onToggleSuggestiveAction}
					onDeleteSuggestiveAction={onDeleteSuggestiveAction}
					onBulkToggleSuggestiveActions={onBulkToggleSuggestiveActions}
					onResetSuggestiveActionsSegment={onResetSuggestiveActionsSegment}
				/>

			<Toast toast={toast} onClose={() => setToast({ message: "", type: "info" })} />
		</div>
	);

	const changePasswordView = (
		<div className="login-screen">
			<div className="login-card" role="dialog" aria-labelledby="password-change-title">
				<p className="login-kicker">Security Update Required</p>
				<h1 id="password-change-title">Change Temporary Password</h1>
				<p className="login-subtext">
					Use your current temporary password, then set a new one to continue.
				</p>
				<form className="login-form" onSubmit={onPasswordChangeSubmit} autoComplete="off">
					<input
						type="password"
						name="current_password"
						value={currentPasswordInput}
						onChange={(event) => setCurrentPasswordInput(event.target.value)}
						placeholder="Current Password"
						autoComplete="current-password"
						required
					/>
					<input
						type="password"
						name="new_password"
						value={newPasswordInput}
						onChange={(event) => setNewPasswordInput(event.target.value)}
						placeholder="New Password"
						autoComplete="new-password"
						required
					/>
					<input
						type="password"
						name="confirm_new_password"
						value={confirmNewPasswordInput}
						onChange={(event) => setConfirmNewPasswordInput(event.target.value)}
						placeholder="Confirm New Password"
						autoComplete="new-password"
						required
					/>
					<button type="submit" disabled={isPasswordChangeSubmitting}>
						{isPasswordChangeSubmitting ? "Updating..." : "Update Password"}
					</button>
				</form>
				{loginError && <p className="login-error">{loginError}</p>}
			</div>
		</div>
	);

	const teamDashboardView = (
		<div className="app-shell">
			<div className="access-shell-toolbar">
				<button className="access-logout-btn" onClick={onLogout}>
					<span className="logout-btn-icon" aria-hidden="true">
						<svg viewBox="0 0 20 20">
							<path d="M8 3h-3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3" />
							<path d="M13 6l4 4-4 4" />
							<path d="M17 10H8" />
						</svg>
					</span>
					<span>Logout</span>
				</button>
			</div>
			<ClientTeamDashboard
				organizations={organizations}
				onNotify={showToast}
				onBack={() => navigate("/analysis")}
			/>
			<Toast toast={toast} onClose={() => setToast({ message: "", type: "info" })} />
		</div>
	);

	return (
		<>
			<PhoneCaptureModal
				isOpen={showPhoneCaptureModal}
				email={authProfile?.email || ""}
				onSubmit={async (phone) => {
					setIsPhoneSubmitting(true);
					try {
						await saveTrialPhone(phone);
						setShowPhoneCaptureModal(false);
						navigate("/analysis", { replace: true });
					} catch (err) {
						showToast(err?.response?.data?.detail || err?.message || "Failed to save phone", "error");
					} finally {
						setIsPhoneSubmitting(false);
					}
				}}
				isSubmitting={isPhoneSubmitting}
			/>
			<Routes>
			<Route
				path="/privacy-policy"
				element={<PrivacyPolicy />}
			/>
			<Route
				path="/"
				element={isAuthenticated ? (!isTrialStatusLoaded ? <AppLoader variant="fullscreen" title="Restoring Workspace" message="Loading your trial analysis access..." /> : <Navigate to={passwordChangeRequired ? "/change-password" : (isTrialAccount ? "/analysis" : "/onboarding")} replace />) : loginView}
			/>
			<Route
				path="/explore"
				element={<StartTrial />}
			/>
			<Route
				path="/callback"
				element={isAuthenticated ? (!isTrialStatusLoaded ? <AppLoader variant="fullscreen" title="Restoring Workspace" message="Loading your trial analysis access..." /> : <Navigate to={passwordChangeRequired ? "/change-password" : (isTrialAccount ? "/analysis" : "/onboarding")} replace />) : loginView}
			/>
			<Route
				path="/whatsapp/oauth/callback"
				element={isAuthenticated ? <Navigate to="/analysis" replace /> : loginView}
			/>
			<Route
				path="/change-password"
				element={
					<ProtectedRoute isAuthenticated={isAuthenticated} isAuthBootstrapping={isAuthBootstrapping}>
						{changePasswordView}
					</ProtectedRoute>
				}
			/>
			<Route
				path="/onboarding"
				element={
					<ProtectedRoute isAuthenticated={isAuthenticated} isAuthBootstrapping={isAuthBootstrapping}>
						{!isTrialStatusLoaded ? <AppLoader variant="fullscreen" title="Restoring Workspace" message="Loading your trial analysis access..." /> : isTrialAccount ? <Navigate to="/analysis" replace /> : onboardingView}
					</ProtectedRoute>
				}
			/>
			<Route
				path="/analysis"
				element={
					<ProtectedRoute
						isAuthenticated={isAuthenticated}
						isAuthBootstrapping={isAuthBootstrapping}
						allowPendingEmbedded={isEmbeddedMode}
					>
						{analysisView}
					</ProtectedRoute>
				}
			/>
			<Route
				path="/admin"
				element={
					<AdminRoute isAuthenticated={isAuthenticated} isAuthBootstrapping={isAuthBootstrapping} isAdmin={isAdmin}>
						{developerView}
					</AdminRoute>
				}
			/>
			<Route
				path="/team"
				element={
					<ClientManagerRoute
						isAuthenticated={isAuthenticated}
						isAuthBootstrapping={isAuthBootstrapping}
						canManageEmployees={canManageEmployees}
					>
						{teamDashboardView}
					</ClientManagerRoute>
				}
			/>
			<Route
				path="*"
				element={isAuthenticated ? (!isTrialStatusLoaded ? <AppLoader variant="fullscreen" title="Restoring Workspace" message="Loading your trial analysis access..." /> : <Navigate to={passwordChangeRequired ? "/change-password" : (isTrialAccount ? "/analysis" : "/onboarding")} replace />) : <Navigate to="/" replace />}
			/>
		</Routes>
		<PhoneCaptureModal
			isOpen={showPhoneCaptureModal}
			email={authProfile?.email || ""}
			onSubmit={async (phone) => {
				setIsPhoneSubmitting(true);
				try {
					await saveTrialPhone(phone);
					setShowPhoneCaptureModal(false);
					navigate("/onboarding", { replace: true });
				} catch (err) {
						showToast(err?.response?.data?.detail || err?.message || "Failed to save phone", "error");
				} finally {
					setIsPhoneSubmitting(false);
				}
			}}
			isSubmitting={isPhoneSubmitting}
		/>
		</>
	);
}
